import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MdAttachFile } from 'react-icons/md';
import api from '../../utils/api';
import { getProjectManagementPaths } from '../../modules/projectManagement/config';
import { canFeatureForRole, PERMISSIONS } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { notifyApiError } from '../../utils/notify';
import { sortUsersByRole } from '../../utils/userSort';
import { getInitials } from '../../utils/getInitials';
import TaskReminderModal from '../../components/modals/TaskReminderModal';
import Pagination from '../../components/common/Pagination';
import SortableTh, { nextSort, sortRows } from '../../components/common/SortableTh';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';
import '../../styles/ProjectStatCards.css';

// Unambiguous "08 Sep 2026" date, identical across browsers/locales.
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatExpiryDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')} ${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const tabs = ['Overview', /* 'Features', */ 'Tasks', 'Files', 'Notes', 'History'];
const TASK_STATUSES = ['Not Started', 'In Progress', 'R&D Development', 'R&D Testing', 'Final Verification', 'Complete', 'Re-open', 'On Hold', 'Invalid Issue'];
// Display labels for status values — 'Complete' is stored in DB but shown as 'Completed' in UI.
const STATUS_DISPLAY_LABEL = { Complete: 'Completed' };
const PRIORITIES = ['Low', 'Medium', 'High'];
const TASK_CATEGORIES = ['Requirement', 'Defect', 'Common Tasks'];
const FEATURE_STATUSES = ['Planned', 'In Progress', 'Completed'];
// const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MAX_TOTAL_BYTES = 500 * 1024 * 1024;

// Edit-task modal only lists a couple of attachments; the rest open on the task view page.
const VISIBLE_ATTACHMENT_LIMIT = 2;

// Max number of NEW files a user can upload at once. Only newly added (pending)
// files count toward this — existing attachments already on the task are ignored.
const MAX_NEW_ATTACHMENTS = 2;
const formatBytes = (b) => {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

const toNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const isTaskComplete = (task) => String(task?.status || '').trim().toLowerCase() === 'complete';

const isProjectCompleted = (status) => {
  const value = String(status || '').trim().toLowerCase();
  return value === 'finished' || value === 'completed' || value === 'complete';
};

const STATUS_PILL_COLORS = {
  'not started': { bg: '#fff7ed', color: '#c05621', border: '#fed7aa', dot: '#dd6b20' },
  'in progress': { bg: '#ebf8ff', color: '#2b6cb0', border: '#bee3f8', dot: '#3182ce' },
  'on hold': { bg: '#fffbeb', color: '#975a16', border: '#fbd38d', dot: '#d69e2e' },
  finished: { bg: '#f0fff4', color: '#276749', border: '#9ae6b4', dot: '#38a169' },
  completed: { bg: '#f0fff4', color: '#276749', border: '#9ae6b4', dot: '#38a169' },
  complete: { bg: '#f0fff4', color: '#276749', border: '#9ae6b4', dot: '#38a169' },
  cancelled: { bg: '#fff5f5', color: '#c53030', border: '#feb2b2', dot: '#e53e3e' },
  invalid: { bg: '#faf5ff', color: '#6b21a8', border: '#e9d8fd', dot: '#9333ea' },
};

const getStatusPalette = (status) => {
  const key = String(status || '').trim().toLowerCase();
  return STATUS_PILL_COLORS[key] || { bg: '#edf2f7', color: '#4a5568', border: '#e2e8f0', dot: '#a0aec0' };
};

const PROJECT_STATUS_DISPLAY = {
  'Not Started': 'Not Started',
  'In Progress': 'In Progress',
  'On Hold': 'On Hold',
  Cancelled: 'Cancelled',
  Finished: 'Completed',
};
const getProjectStatusLabel = (status) => PROJECT_STATUS_DISPLAY[status] || status || 'Unknown';

const getStatusPillStyle = (status) => {
  const c = getStatusPalette(status);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    padding: '7px 14px',
    borderRadius: '999px',
    background: c.bg,
    color: c.color,
    border: `1px solid ${c.border}`,
    fontSize: '12px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  };
};

const isPastDueTask = (task) => {
  if (!task?.dueDate) return false;
  if (isTaskComplete(task)) return false;
  const due = new Date(task.dueDate);
  if (Number.isNaN(due.getTime())) return false;
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
};

// const startOfIsoWeekMonday = (base = new Date()) => {
//   const d = new Date(base);
//   const day = d.getDay();
//   const diff = day === 0 ? -6 : 1 - day;
//   d.setDate(d.getDate() + diff);
//   d.setHours(0, 0, 0, 0);
//   return d;
// };

// const endOfIsoWeekSunday = (mondayStart) => {
//   const e = new Date(mondayStart);
//   e.setDate(e.getDate() + 6);
//   e.setHours(23, 59, 59, 999);
//   return e;
// };

// const spentHoursFromActivityLog = (log) => {
//   if (String(log?.entityType || '') !== 'task') return 0;
//   const changes = log.changes || [];
//   const c = changes.find((x) => x.field === 'yourActualHour') || changes.find((x) => x.field === 'actualHour');
//   if (!c) return 0;
//   let spent = toNum(c.to) - toNum(c.from);
//   if (String(log.action || '') === 'timer_stopped' && spent < 0) spent = 0;
//   if (Math.abs(spent) < 0.000001) return 0;
//   return spent;
// };

const calendarDaysBetween = (a, b) => {
  const start = new Date(a);
  const end = new Date(b);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.round((end - start) / 86400000);
};

// 👇 builds a map of managerId -> [directReportIds] from the users list (uses user.manager field)
const buildHierarchyMap = (users) => {
  const map = {};
  users.forEach((u) => {
    const userId = u._id || u.id;
    const managerId = (u.manager && (u.manager._id || u.manager.id || u.manager)) || '';
    if (!managerId) return;
    if (!map[managerId]) map[managerId] = [];
    map[managerId].push(userId);
  });
  return map;
};

// 👇 walks the hierarchy map to find every user under a given leader (direct + indirect reports)
const getDescendantIds = (leaderId, hierarchyMap) => {
  if (!leaderId) return [];
  const result = [];
  const queue = [...(hierarchyMap[leaderId] || [])];
  while (queue.length) {
    const current = queue.shift();
    if (result.includes(current)) continue;
    result.push(current);
    if (hierarchyMap[current]) queue.push(...hierarchyMap[current]);
  }
  return result;
};

const UserMultiSelectDropdown = ({
  label,
  placeholder,
  users,
  selectedIds,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  // Live mirror of the selection, read by the ordering memo below without being one of its deps.
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const filteredUsers = useMemo(() => {
    const value = search.trim().toLowerCase();
    const list = !value
      ? users
      : users.filter((user) =>
          `${user.name || ''} ${user.email || ''} ${user.role || ''}`.toLowerCase().includes(value)
        );
    const sorted = sortUsersByRole(list);
    // Selected users float to the top so the current selection is always visible first. This order is
    // snapshotted when the dropdown opens or the search changes — NOT on every toggle — otherwise
    // ticking a checkbox reshuffles the rows and loses the user's scroll position mid-selection.
    const selectedSet = new Set((selectedIdsRef.current || []).map((id) => String(id)));
    const selected = sorted.filter((user) => selectedSet.has(String(user._id || user.id)));
    const rest = sorted.filter((user) => !selectedSet.has(String(user._id || user.id)));
    return [...selected, ...rest];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, search, open]);

  const selectedUsers = users.filter((user) => selectedIds.includes(user._id || user.id));

  const toggleUser = (userId) => {
    if (selectedIds.includes(userId)) {
      onChange(selectedIds.filter((id) => id !== userId));
      return;
    }
    onChange([...selectedIds, userId]);
  };

  const filteredIds = filteredUsers.map((user) => user._id || user.id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      onChange(selectedIds.filter((id) => !filteredIds.includes(id)));
    } else {
      onChange([...new Set([...selectedIds, ...filteredIds])]);
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', minWidth: 0 }}>
      <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>{label}</div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          ...searchInputStyle,
          width: '100%',
          minWidth: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
        }}
      >
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedUsers.length > 0 ? selectedUsers.map((user) => user.name).join(', ') : placeholder}
        </span>
        <span style={{ flexShrink: 0, fontSize: '12px', color: '#718096' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={userDropdownPanelStyle}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <input
              placeholder={`Search ${(label || 'users').toLowerCase()}...`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ ...searchInputStyle, minWidth: 0, flex: 1 }}
            />
            {filteredUsers.length > 0 && (
              <button type="button" onClick={toggleSelectAll} style={{ ...selectAllBtnStyle, marginBottom: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
          <div style={{ maxHeight: '170px', overflowY: 'auto', borderTop: '1px solid #edf2f7', paddingTop: '8px' }}>
            {filteredUsers.map((user) => {
              const userId = user._id || user.id;
              const checked = selectedIds.includes(userId);
              return (
                <label
                  key={userId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 2px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f7fafc',
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleUser(userId)} />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '13px', color: '#2d3748', fontWeight: 600 }}>
                      {user.name || 'Unnamed User'} ({user.role || '-'})
                    </span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const ProjectViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const projectManagementPaths = getProjectManagementPaths(
    location.pathname.startsWith('/employee') ? 'employee' : 'admin'
  );
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}');
    } catch (error) {
      return {};
    }
  }, []);
  const currentUserId = currentUser?._id || currentUser?.id || '';
  const currentUserRole = String(currentUser?.role || '').toLowerCase();
  const isAdminUser = ['admin', 'hod', 'manager'].includes(currentUserRole);

  // Settings → User Roles matrix drives edit/add/delete affordances (matches backend enforcement).
  const { rolePermissions } = useRolePermissionMatrix();
  const canEditProjectPerm = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.EDIT_PROJECT]);
  const canAddTaskPerm = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.ADD_TASK]);
  const canEditTaskPerm = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.EDIT_TASK]);
  const canDeleteTaskPerm = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.DELETE_TASK]);

  // Overview fields (team leader, status, deadline, members) require the Edit Project permission.
  const canEditOverview = canEditProjectPerm;
  const [activeTab, setActiveTab] = useState('Overview');
  const activeTabRef = useRef('Overview');
  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [noteInput, setNoteInput] = useState('');
  const [searchTask, setSearchTask] = useState('');
  const [searchFeature, setSearchFeature] = useState('');
  const [taskFeatureFilter, setTaskFeatureFilter] = useState('All');
  const [featureStatusCardFilter, setFeatureStatusCardFilter] = useState('All');
  const [taskStatusCardFilter, setTaskStatusCardFilter] = useState([]);
  const [taskOverdueOnly, setTaskOverdueOnly] = useState(false);
  const [taskCurrentPage, setTaskCurrentPage] = useState(1);
  const [taskItemsPerPage, setTaskItemsPerPage] = useState(getDefaultItemsPerPageSync());
  // Tasks tab column sort. Empty sortBy = the API's order (newest first).
  const [taskSort, setTaskSort] = useState({ sortBy: '', sortOrder: 'asc' });
  const [searchFile, setSearchFile] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [uploadingProjectFiles, setUploadingProjectFiles] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmDeleteProjectFileId, setConfirmDeleteProjectFileId] = useState('');
  const [deletingProjectFileId, setDeletingProjectFileId] = useState('');
  const [fileCommentText, setFileCommentText] = useState('');
  const [submittingFileComment, setSubmittingFileComment] = useState(false);
  const [editingFileCommentId, setEditingFileCommentId] = useState('');
  const [editingFileCommentText, setEditingFileCommentText] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState('');
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [removingAttachmentId, setRemovingAttachmentId] = useState('');
  const [assigneeActualHoursDraft, setAssigneeActualHoursDraft] = useState({});
  const [deletingTaskId, setDeletingTaskId] = useState('');
  const [remindingTaskId, setRemindingTaskId] = useState('');
  const [reminderTask, setReminderTask] = useState(null);
  const [statusUpdatingTaskId, setStatusUpdatingTaskId] = useState('');
  const [confirmDeleteTaskId, setConfirmDeleteTaskId] = useState('');
  const [showAddFeatureModal, setShowAddFeatureModal] = useState(false);
  const [showFeatureExportMenu, setShowFeatureExportMenu] = useState(false);
  const [showTaskExportMenu, setShowTaskExportMenu] = useState(false);
  const [savingFeature, setSavingFeature] = useState(false);
  const [editingFeatureId, setEditingFeatureId] = useState('');
  const [deletingFeatureId, setDeletingFeatureId] = useState('');
  const [confirmDeleteFeatureId, setConfirmDeleteFeatureId] = useState('');
  const [projectActivityLogs, setProjectActivityLogs] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [overviewMetricsLogs, setOverviewMetricsLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [editingOverviewStatus, setEditingOverviewStatus] = useState(false);
  const [editingOverviewTeamLeader, setEditingOverviewTeamLeader] = useState(false);
  const [editingOverviewMembers, setEditingOverviewMembers] = useState(false);
  // Members source: 'hierarchy' = only the Project Leader's team, 'all' = any user in the system.
  const [overviewMemberSource, setOverviewMemberSource] = useState('hierarchy');
  const [editingOverviewFollowers, setEditingOverviewFollowers] = useState(false);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [showAllFollowers, setShowAllFollowers] = useState(false);
  const [editingOverviewDeadline, setEditingOverviewDeadline] = useState(false);
  const [editingOverviewName, setEditingOverviewName] = useState(false);
  const [editingOverviewStartDate, setEditingOverviewStartDate] = useState(false);
  const [editingOverviewDescription, setEditingOverviewDescription] = useState(false);
  const [overviewStatusDraft, setOverviewStatusDraft] = useState('Not Started');
  const [overviewTeamLeaderDraft, setOverviewTeamLeaderDraft] = useState('');
  const [overviewMembersDraft, setOverviewMembersDraft] = useState([]);
  const [overviewFollowersDraft, setOverviewFollowersDraft] = useState([]);
  const [overviewDeadlineDraft, setOverviewDeadlineDraft] = useState('');
  const [overviewNameDraft, setOverviewNameDraft] = useState('');
  const [overviewStartDateDraft, setOverviewStartDateDraft] = useState('');
  const [overviewDescriptionDraft, setOverviewDescriptionDraft] = useState('');
  const [savingOverview, setSavingOverview] = useState(false);
  const [newTask, setNewTask] = useState({
    name: '',
    status: 'Not Started',
    priority: 'Medium',
    taskCategory: 'Requirement',
    public: false,
    billable: false,
    startDate: '',
    dueDate: '',
    featureId: '',
    assignees: currentUserId ? [currentUserId] : [],
    followers: [],
    hour: '',
    actualHour: '',
    taskDescription: '',
    comment: '',
  });
  const [newFeature, setNewFeature] = useState({
    name: '',
    description: '',
    totalEstimatedTime: '',
    totalActualTime: '',
    status: 'Planned',
  });

  useEffect(() => {
    loadProject();
    loadUsers();
  }, [id]);

  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== taskItemsPerPage) {
        setTaskItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTaskCurrentPage(1);
  }, [searchTask, taskFeatureFilter, taskStatusCardFilter, taskOverdueOnly, taskItemsPerPage, taskSort]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const all = [];
        let page = 1;
        const limit = 200;
        let totalPages = 1;
        do {
          const res = await api.getProjectActivity(id, { page, limit });
          if (!res?.success || cancelled) break;
          all.push(...(res.data || []));
          const total = res.total ?? all.length;
          totalPages = Math.max(1, Math.ceil(total / limit));
          page += 1;
        } while (page <= totalPages && page <= 15);
        if (!cancelled) setOverviewMetricsLogs(all);
      } catch (_) {
        if (!cancelled) setOverviewMetricsLogs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    activeTabRef.current = activeTab;
    if (activeTab === 'History') {
      loadProjectActivity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadProject = async () => {
    try {
      const response = await api.getProject(id);
      if (response.success) {
        setProject(response.data);
        setNoteInput('');
        if (activeTabRef.current === 'History') {
          loadProjectActivity(response.data._id);
        }
        return response.data;
      }
    } catch (error) {
      notifyApiError(error, 'Failed to load project');
    }
    return null;
  };

  const loadUsers = async () => {
    try {
      const response = await api.getUsers({ limit: 1000, isActive: 'true' });
      if (!response.success) return;
      setUsers(response.data || []);
    } catch (error) {
      notifyApiError(error, 'Failed to load users');
    }
  };

  const taskRows = project?.tasks || [];
  const featureRows = project?.features || [];
  const fileRows = project?.files || [];
  const projectMemberIds = useMemo(
    () => (project?.members || []).map((member) => String(member._id || member.id)).filter(Boolean),
    [project]
  );
  const projectMembers = useMemo(
    () => users.filter((user) => projectMemberIds.includes(String(user._id || user.id))),
    [users, projectMemberIds]
  );
  const projectFollowerIds = useMemo(
    () => (project?.followers || []).map((follower) => String(follower?._id || follower?.id || follower || '')).filter(Boolean),
    [project]
  );
  const featureMap = useMemo(
    () => Object.fromEntries((featureRows || []).map((feature) => [String(feature._id), feature.name])),
    [featureRows]
  );
  const projectNotes = useMemo(() => {
    const history = Array.isArray(project?.notesHistory) ? project.notesHistory : [];
    if (history.length > 0) {
      return history.slice().sort((a, b) => new Date(b.notedAt || 0) - new Date(a.notedAt || 0));
    }
    if (project?.notes) {
      return [
        {
          _id: 'legacy-note',
          text: project.notes,
          notedAt: project.updatedAt || project.createdAt,
          notedBy: project.updatedBy || project.createdBy,
        },
      ];
    }
    return [];
  }, [project]);

  useEffect(() => {
    if (!project) return;
    setOverviewStatusDraft(project.status || 'Not Started');
    setOverviewTeamLeaderDraft(project.teamLeader?._id || project.teamLeader?.id || '');
    setOverviewMembersDraft((project.members || []).map((member) => member._id || member.id).filter(Boolean));
    setOverviewFollowersDraft((project.followers || []).map((follower) => follower._id || follower.id).filter(Boolean));
    setOverviewDeadlineDraft(
      project.deadline ? new Date(project.deadline).toISOString().slice(0, 10) : ''
    );
    setOverviewNameDraft(project.name || '');
    setOverviewStartDateDraft(
      project.startDate ? new Date(project.startDate).toISOString().slice(0, 10) : ''
    );
    setOverviewDescriptionDraft(project.description || '');
  }, [project]);

  const filteredTasks = useMemo(() => {
    const value = searchTask.trim().toLowerCase();
    return taskRows.filter((row) => {
      // Legacy 'Invalid' tasks are treated as the renamed 'Invalid Issue' status.
      const rowStatus = String(row.status || '') === 'Invalid' ? 'Invalid Issue' : String(row.status || '');
      if (taskStatusCardFilter.length > 0 && !taskStatusCardFilter.includes(rowStatus)) return false;
      if (taskOverdueOnly && !isPastDueTask(row)) return false;
      const featureId = String(row.featureId || '');
      const passesFeature =
        taskFeatureFilter === 'All' ||
        (taskFeatureFilter === 'No Feature' ? !featureId : featureId === taskFeatureFilter);
      if (!passesFeature) return false;
      if (!value) return true;
      return `${row.name} ${row.status} ${row.assignedTo?.name || ''} ${(row.tags || []).join(' ')} ${featureMap[featureId] || ''}`
        .toLowerCase()
        .includes(value);
    });
  }, [searchTask, taskRows, featureMap, taskFeatureFilter, taskStatusCardFilter, taskOverdueOnly]);

  const sortedTasks = useMemo(() => {
    const toTime = (value) => {
      const time = value ? new Date(value).getTime() : NaN;
      return Number.isNaN(time) ? null : time;
    };
    // Number of assignees the row shows (same resolution as the Assigned To cell).
    const assigneeCount = (row) => {
      const count = (Array.isArray(row.assignees) ? row.assignees : []).filter((assignee) => {
        if (assignee && typeof assignee === 'object') return Boolean(assignee.name || assignee.email);
        const user = users.find((u) => String(u._id || u.id) === String(assignee || ''));
        return Boolean(user?.name || user?.email);
      }).length;
      return count || (row.assignedTo?.name ? 1 : 0);
    };
    // Status/priority follow workflow order rather than the alphabet; legacy 'Invalid' ranks as 'Invalid Issue'.
    const rank = (order, value) => (order.indexOf(value) === -1 ? order.length : order.indexOf(value));
    return sortRows(filteredTasks, taskSort, {
      taskId: (row) => row.taskCode || null,
      name: (row) => row.name || null,
      assignee: assigneeCount,
      priority: (row) => rank(PRIORITIES, row.priority || 'Medium'),
      status: (row) => rank(TASK_STATUSES, row.status === 'Invalid' ? 'Invalid Issue' : row.status),
      startDate: (row) => toTime(row.startDate),
      dueDate: (row) => toTime(row.dueDate),
    });
  }, [filteredTasks, taskSort, users]);

  const taskTotalPages = Math.max(1, Math.ceil(sortedTasks.length / taskItemsPerPage));

  const paginatedTasks = useMemo(() => {
    const startIndex = (taskCurrentPage - 1) * taskItemsPerPage;
    return sortedTasks.slice(startIndex, startIndex + taskItemsPerPage);
  }, [sortedTasks, taskCurrentPage, taskItemsPerPage]);

  useEffect(() => {
    if (taskCurrentPage > taskTotalPages) {
      setTaskCurrentPage(taskTotalPages);
    }
  }, [taskCurrentPage, taskTotalPages]);

  // Drive the RESET buttons: red while something the button would clear is set, grey/disabled otherwise.
  const hasActiveTaskFilters =
    Boolean(searchTask.trim()) ||
    taskFeatureFilter !== 'All' ||
    taskStatusCardFilter.length > 0 ||
    taskOverdueOnly;

  const hasActiveFeatureFilters = Boolean(searchFeature.trim()) || featureStatusCardFilter !== 'All';

  const featureSummary = useMemo(() => {
    const base = FEATURE_STATUSES.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
    featureRows.forEach((feature) => {
      const status = feature.status || 'Planned';
      base[status] = (base[status] || 0) + 1;
    });
    return base;
  }, [featureRows]);

  const filteredFeatureRows = useMemo(() => {
    const value = searchFeature.trim().toLowerCase();
    return featureRows.filter((feature) => {
      if (featureStatusCardFilter !== 'All' && String(feature.status || '') !== featureStatusCardFilter) return false;
      if (!value) return true;
      return `${feature.featureCode || ''} ${feature.name || ''} ${feature.status || ''} ${feature.description || ''} ${feature.createdBy?.name || ''}`
        .toLowerCase()
        .includes(value);
    });
  }, [featureRows, featureStatusCardFilter, searchFeature]);

  const filteredFiles = useMemo(() => {
    const value = searchFile.trim().toLowerCase();
    if (!value) return fileRows;
    return fileRows.filter((row) =>
      `${row.fileName} ${row.fileType} ${row.uploadedBy?.name || ''}`.toLowerCase().includes(value)
    );
  }, [searchFile, fileRows]);

  const projectHistoryRows = useMemo(
    () => (projectActivityLogs || []).filter((entry) => String(entry?.entityType || '') !== 'feature'),
    [projectActivityLogs]
  );

  const saveOverviewField = async (payload, message) => {
    try {
      setSavingOverview(true);
      await api.updateProject(project._id, payload);
      toast.success(message || 'Project updated');
      await loadProject();
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to update project');
      return false;
    } finally {
      setSavingOverview(false);
    }
  };

  const taskSummary = useMemo(() => {
    const base = {
      'Not Started': 0,
      'In Progress': 0,
      'On Hold': 0,
      'R&D Development': 0,
      'R&D Testing': 0,
      'Final Verification': 0,
      Complete: 0,
      'Re-open': 0,
      'Invalid Issue': 0,
    };
    taskRows.forEach((task) => {
      // Legacy 'Invalid' tasks count under the renamed 'Invalid Issue' bucket.
      const status = (task.status === 'Invalid' ? 'Invalid Issue' : task.status) || 'Not Started';
      base[status] = (base[status] || 0) + 1;
    });
    return base;
  }, [taskRows]);

  const overdueTaskCount = useMemo(
    () => taskRows.reduce((count, task) => (isPastDueTask(task) ? count + 1 : count), 0),
    [taskRows]
  );

  const overviewOpenTasks = useMemo(() => {
    const total = taskRows.length;
    const open = taskRows.filter((task) => !isTaskComplete(task)).length;
    const barPct = total ? Math.round((open / total) * 100) : 0;
    return { open, total, barPct };
  }, [taskRows]);

  const overviewTimeline = useMemo(() => {
    const start = project?.startDate ? new Date(project.startDate) : null;
    const end = project?.deadline ? new Date(project.deadline) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return {
        totalPlanDays: 0,
        remainingDays: 0,
        scheduleUsedPct: 0,
        hasDates: false,
      };
    }
    const start0 = new Date(start);
    start0.setHours(0, 0, 0, 0);
    const end0 = new Date(end);
    end0.setHours(0, 0, 0, 0);
    if (end0 < start0) {
      return {
        totalPlanDays: 0,
        remainingDays: 0,
        scheduleUsedPct: 0,
        hasDates: false,
      };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Inclusive window (start through deadline, both dates count).
    const totalPlanDays = Math.max(1, calendarDaysBetween(start0, end0) + 1);
    // Progress bar: calendar days elapsed within the plan (today counts toward used time).
    let usedDaysForBar = 0;
    if (today < start0) {
      usedDaysForBar = 0;
    } else if (today > end0) {
      usedDaysForBar = totalPlanDays;
    } else {
      usedDaysForBar = Math.min(totalPlanDays, calendarDaysBetween(start0, today) + 1);
    }
    const scheduleUsedPct = Math.min(100, Math.round((usedDaysForBar / totalPlanDays) * 100));
    // "Days left" label: do not count today — count full days from tomorrow through deadline (inclusive).
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const remainingFrom = tomorrow > start0 ? tomorrow : start0;
    let remainingDays = 0;
    if (remainingFrom > end0) {
      remainingDays = 0;
    } else {
      remainingDays = calendarDaysBetween(remainingFrom, end0) + 1;
    }
    return { totalPlanDays, remainingDays, scheduleUsedPct, hasDates: true };
  }, [project?.startDate, project?.deadline]);

  const isOverviewDeadlineMissed = useMemo(() => {
    if (!project?.deadline) return false;
    if (isProjectCompleted(project?.status)) return false;
    const deadline = new Date(project.deadline);
    if (Number.isNaN(deadline.getTime())) return false;
    deadline.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today > deadline;
  }, [project?.deadline, project?.status]);

  const overviewDisplayProgress = useMemo(() => {
    if (!taskRows.length) return 0;
    const done = taskRows.filter((task) => isTaskComplete(task)).length;
    return Math.min(100, (done / taskRows.length) * 100);
  }, [taskRows]);

  const overviewProgressPctLabel = useMemo(
    () => `${Number(overviewDisplayProgress || 0).toFixed(2)}%`,
    [overviewDisplayProgress]
  );

  // 👇 full manager -> reports hierarchy map, built once from the users list
  const hierarchyMap = useMemo(() => buildHierarchyMap(users), [users]);

  // 👇 members list is restricted to whoever falls under the selected Project Leader's hierarchy
  const eligibleMemberUsers = useMemo(() => {
    if (!overviewTeamLeaderDraft) return [];
    const descendantIds = getDescendantIds(overviewTeamLeaderDraft, hierarchyMap);
    return users.filter((user) => descendantIds.includes(user._id || user.id));
  }, [users, overviewTeamLeaderDraft, hierarchyMap]);

  // Live mirror of the members draft, read by the option-list memo below without being one of its deps.
  const overviewMembersDraftRef = useRef(overviewMembersDraft);
  overviewMembersDraftRef.current = overviewMembersDraft;

  // 👇 what the Members picker offers: the leader's hierarchy, or every user when the source is 'all'.
  // In hierarchy mode already-selected users are kept so out-of-hierarchy members stay visible/removable.
  // Keyed on editingOverviewMembers (not on the draft itself) so this array keeps a stable identity for
  // the whole edit session — otherwise every checkbox toggle hands the picker a brand-new `users` array,
  // which re-runs its ordering memo, floats the just-picked user to the top and loses the scroll position.
  const memberOptionUsers = useMemo(() => {
    if (overviewMemberSource === 'all') return users;
    const eligibleIds = new Set(eligibleMemberUsers.map((user) => String(user._id || user.id)));
    const selectedSet = new Set((overviewMembersDraftRef.current || []).map((id) => String(id)));
    return [
      ...eligibleMemberUsers,
      ...users.filter((user) => {
        const id = String(user._id || user.id);
        return selectedSet.has(id) && !eligibleIds.has(id);
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, eligibleMemberUsers, overviewMemberSource, editingOverviewMembers]);

  // const weekLoggedHoursByDay = useMemo(() => {
  //   const hours = [0, 0, 0, 0, 0, 0, 0];
  //   const weekStart = startOfIsoWeekMonday();
  //   const weekEnd = endOfIsoWeekSunday(weekStart);
  //   (overviewMetricsLogs || []).forEach((log) => {
  //     const at = new Date(log?.changedAt);
  //     if (Number.isNaN(at.getTime())) return;
  //     if (at < weekStart || at > weekEnd) return;
  //     const spent = spentHoursFromActivityLog(log);
  //     if (spent <= 0) return;
  //     const dow = at.getDay();
  //     const idx = dow === 0 ? 6 : dow - 1;
  //     hours[idx] += spent;
  //   });
  //   const maxH = Math.max(...hours, 0.0001);
  //   return WEEKDAY_LABELS.map((label, idx) => ({
  //     label,
  //     hours: hours[idx],
  //     barPct: Math.min(100, Math.round((hours[idx] / maxH) * 100)),
  //   }));
  // }, [overviewMetricsLogs]);

  if (!project) {
    return <div style={{ padding: '16px' }}>Loading project...</div>;
  }

  const handleAddTask = async () => {
    if (!newTask.name.trim()) {
      toast.warning('Task name is required');
      return;
    }
    if (newTask.startDate && newTask.dueDate) {
      const start = new Date(newTask.startDate);
      const due = new Date(newTask.dueDate);
      start.setHours(0, 0, 0, 0);
      due.setHours(0, 0, 0, 0);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(due.getTime()) && due < start) {
        toast.warning('Due date cannot be before the start date');
        return;
      }
    }
    try {
      setSavingTask(true);
      const payload = {
        name: newTask.name.trim(),
        status: newTask.status,
        priority: newTask.priority,
        taskCategory: newTask.taskCategory,
        public: Boolean(newTask.public),
        billable: Boolean(newTask.billable),
        startDate: newTask.startDate || undefined,
        dueDate: newTask.dueDate || undefined,
        featureId: newTask.featureId,
        assignees: newTask.assignees,
        assignedTo: newTask.assignees[0] || undefined,
        followers: newTask.followers,
        hour: Number(newTask.hour || 0),
        taskDescription: newTask.taskDescription,
      };
      if (editingTaskId && !isAdminUser) {
        const isCurrentUserAssignee = (newTask.assignees || []).some(
          (userId) => String(userId) === String(currentUserId || '')
        );
        if (isCurrentUserAssignee) {
          payload.actualHourScope = 'self';
          payload.actualHour = Number(newTask.actualHour || 0);
        }
      }
      if (!editingTaskId) {
        payload.actualHour = Number(newTask.actualHour || 0);
      }
      if (editingTaskId && isAdminUser) {
        payload.assigneeActualHours = newTask.assignees.map((userId) => ({
          userId,
          hour: Number(assigneeActualHoursDraft[String(userId)] || 0),
        }));
      }
      let taskIdForAttachments = editingTaskId;
      if (editingTaskId) {
        await api.updateTask(editingTaskId, payload);
        toast.success('Task updated');
      } else {
        const response = await api.addProjectTask(project._id, payload);
        taskIdForAttachments = String(response?.data?._id || response?.data?.task?._id || '');
        toast.success('Task added');
      }
      if (pendingAttachments.length > 0 && taskIdForAttachments) {
        try {
          await api.uploadTaskAttachments(taskIdForAttachments, pendingAttachments);
        } catch (uploadError) {
          toast.error(uploadError.message || 'Task saved, but attachment upload failed');
        }
      }
      if ((newTask.comment || '').trim() && taskIdForAttachments) {
        try {
          await api.addTaskComment(taskIdForAttachments, newTask.comment.trim());
        } catch (commentError) {
          toast.error(commentError.message || 'Task saved, but comment could not be added');
        }
      }
      setShowAddTaskModal(false);
      setEditingTaskId('');
      setPendingAttachments([]);
      setExistingAttachments([]);
      setAssigneeActualHoursDraft({});
      setNewTask({
        name: '',
        status: 'Not Started',
        priority: 'Medium',
        taskCategory: 'Requirement',
        public: false,
        billable: false,
        startDate: '',
        dueDate: '',
        featureId: '',
        assignees: currentUserId && projectMemberIds.includes(String(currentUserId)) ? [currentUserId] : [],
        followers: [],
        hour: '',
        actualHour: '',
        taskDescription: '',
        comment: '',
      });
      loadProject();
    } catch (error) {
      toast.error(error.message || `Failed to ${editingTaskId ? 'update' : 'add'} task`);
    } finally {
      setSavingTask(false);
    }
  };

  const handleSaveFeature = async () => {
    if (!newFeature.name.trim()) {
      toast.warning('Feature name is required');
      return;
    }
    try {
      setSavingFeature(true);
      const payload = {
        name: newFeature.name.trim(),
        description: newFeature.description,
        totalEstimatedTime: Number(newFeature.totalEstimatedTime || 0),
        totalActualTime: Number(newFeature.totalActualTime || 0),
        status: newFeature.status,
      };
      if (editingFeatureId) {
        await api.updateProjectFeature(project._id, editingFeatureId, payload);
        toast.success('Feature updated');
      } else {
        await api.addProjectFeature(project._id, payload);
        toast.success('Feature created');
      }
      setShowAddFeatureModal(false);
      setEditingFeatureId('');
      setNewFeature({ name: '', description: '', totalEstimatedTime: '', totalActualTime: '', status: 'Planned' });
      loadProject();
    } catch (error) {
      toast.error(error.message || `Failed to ${editingFeatureId ? 'update' : 'create'} feature`);
    } finally {
      setSavingFeature(false);
    }
  };

  const handleEditFeature = (feature) => {
    setEditingFeatureId(feature._id);
    setNewFeature({
      name: feature.name || '',
      description: feature.description || '',
      totalEstimatedTime: feature.totalEstimatedTime || '',
      totalActualTime: feature.totalActualTime || '',
      status: feature.status || 'Planned',
    });
    setShowAddFeatureModal(true);
  };

  const handleDeleteFeature = async (featureId) => {
    if (!featureId) return;
    try {
      setDeletingFeatureId(featureId);
      await api.deleteProjectFeature(project._id, featureId);
      toast.success('Feature deleted');
      setConfirmDeleteFeatureId('');
      await loadProject();
    } catch (error) {
      toast.error(error.message || 'Failed to delete feature');
    } finally {
      setDeletingFeatureId('');
    }
  };

  const handleCreateTaskFromFeature = (feature) => {
    setEditingTaskId('');
    setAssigneeActualHoursDraft({});
    setNewTask({
      name: '',
      status: 'Not Started',
      priority: 'Medium',
      taskCategory: 'Requirement',
      startDate: '',
      dueDate: '',
      featureId: feature?._id || '',
      assignees: currentUserId && projectMemberIds.includes(String(currentUserId)) ? [currentUserId] : [],
      followers: projectFollowerIds,
      hour: '',
      actualHour: '',
      taskDescription: '',
      comment: '',
    });
    setPendingAttachments([]);
    setExistingAttachments([]);
    setShowAddTaskModal(true);
  };

  const handleEditTask = (task) => {
    if (!task) return;
    setEditingTaskId(task._id || task.id || '');
    const assigneeIds = (task.assignees || [])
      .map((user) => String(user?._id || user?.id || user?.userId || user || '').trim())
      .filter(Boolean);
    const hoursMap = {};
    assigneeIds.forEach((uid) => {
      const row = (task.actualHoursByUser || []).find(
        (entry) => String(entry?.user?._id || entry?.user || '') === uid
      );
      hoursMap[uid] = Number(row?.hour || 0);
    });
    setAssigneeActualHoursDraft(hoursMap);
    const ownActualRow = (task.actualHoursByUser || []).find(
      (entry) => String(entry?.user?._id || entry?.user || '') === String(currentUserId || '')
    );
    const ownActualHour = Number(ownActualRow?.hour || 0);
    const isCurrentUserAssignee =
      assigneeIds.includes(String(currentUserId || '')) || Boolean(ownActualRow);
    setNewTask({
      name: task.name || '',
      status: task.status || 'Not Started',
      priority: task.priority || 'Medium',
      taskCategory: task.taskCategory || 'Requirement',
      public: Boolean(task.public),
      billable: Boolean(task.billable),
      startDate: task.startDate ? new Date(task.startDate).toISOString().slice(0, 10) : '',
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : '',
      featureId: task.featureId || '',
      assignees: (task.assignees || []).map((user) => user?._id || user?.id).filter(Boolean),
      followers: (task.followers || []).map((user) => user?._id || user?.id).filter(Boolean),
      hour: task.hour || '',
      actualHour:
        !isAdminUser
          ? (isCurrentUserAssignee ? ownActualHour : Number(task.actualHour || 0))
          : (task.actualHour || ''),
      taskDescription: task.taskDescription || '',
      comment: '',
    });
    setPendingAttachments([]);
    setExistingAttachments(task.attachments || []);
    setShowAddTaskModal(true);
  };

  const handleRemoveExistingAttachment = async (attachmentId) => {
    if (!attachmentId || !editingTaskId) return;
    const confirmed = window.confirm('Remove this attachment?');
    if (!confirmed) return;
    try {
      setRemovingAttachmentId(String(attachmentId));
      await api.deleteTaskAttachment(editingTaskId, attachmentId);
      toast.success('Attachment removed');
      setExistingAttachments((prev) => prev.filter((file) => String(file._id) !== String(attachmentId)));
      loadProject();
    } catch (error) {
      toast.error(error.message || 'Failed to remove attachment');
    } finally {
      setRemovingAttachmentId('');
    }
  };

  const handleDeleteTask = async (task) => {
    const taskId = task?._id || task?.id;
    if (!taskId) return;
    try {
      setDeletingTaskId(String(taskId));
      await api.deleteTask(taskId);
      toast.success('Task deleted');
      setConfirmDeleteTaskId('');
      await loadProject();
    } catch (error) {
      toast.error(error.message || 'Failed to delete task');
    } finally {
      setDeletingTaskId('');
    }
  };

  // Open the reminder composer for a task (edit message + pick recipients).
  const handleSendReminder = (task) => {
    if (!task?._id && !task?.id) return;
    setReminderTask(task);
  };

  // Normalise a task's assignees into { id, name, email } for the reminder modal.
  const getReminderAssignees = (task) => {
    const list = Array.isArray(task?.assignees) ? task.assignees : [];
    const map = new Map();
    list.forEach((a) => {
      if (a && typeof a === 'object') {
        const id = String(a._id || a.id || '');
        if (id) map.set(id, { id, name: a.name || a.email || 'User', email: a.email || '' });
      } else if (a) {
        const id = String(a);
        map.set(id, { id, name: 'User', email: '' });
      }
    });
    if (task?.assignedTo && typeof task.assignedTo === 'object') {
      const id = String(task.assignedTo._id || task.assignedTo.id || '');
      if (id && !map.has(id)) {
        map.set(id, { id, name: task.assignedTo.name || task.assignedTo.email || 'User', email: task.assignedTo.email || '' });
      }
    }
    return [...map.values()];
  };

  const submitReminder = async ({ note, recipientIds }) => {
    const taskId = reminderTask?._id || reminderTask?.id;
    if (!taskId) return;
    try {
      setRemindingTaskId(String(taskId));
      const res = await api.sendTaskReminder(taskId, { note, recipientIds });
      toast.success(res?.message || 'Reminder sent');
      setReminderTask(null);
    } catch (error) {
      toast.error(error.message || 'Failed to send reminder');
    } finally {
      setRemindingTaskId('');
    }
  };

  // Inline status change straight from the task table (mirrors the Tasks module list behaviour).
  const updateTaskStatusFromTable = async (task, targetStatus) => {
    const id = String(task?._id || task?.id || '');
    if (!id || !targetStatus || task.status === targetStatus) return;
    try {
      setStatusUpdatingTaskId(id);
      await api.updateTask(id, { status: targetStatus });
      toast.success(`Status updated to ${targetStatus}`);
      await loadProject();
    } catch (error) {
      toast.error(error.message || 'Failed to update task status');
    } finally {
      setStatusUpdatingTaskId('');
    }
  };

  // Inline priority change straight from the task table (mirrors the inline status dropdown).
  const updateTaskPriorityFromTable = async (task, targetPriority) => {
    const id = String(task?._id || task?.id || '');
    if (!id || !targetPriority || task.priority === targetPriority) return;
    try {
      setStatusUpdatingTaskId(id);
      await api.updateTask(id, { priority: targetPriority });
      toast.success(`Priority set to ${targetPriority}`);
      await loadProject();
    } catch (error) {
      toast.error(error.message || 'Failed to update task priority');
    } finally {
      setStatusUpdatingTaskId('');
    }
  };

  // Edit is governed purely by the Edit Task permission (matches the backend, which checks
  // edit_task by role — not whether you're an assignee).
  const canEditTask = () => canEditTaskPerm;

  const loadProjectActivity = async (projectId) => {
    const pid = projectId || project?._id;
    if (!pid) return;
    try {
      setActivityLoading(true);
      const res = await api.getProjectActivity(pid);
      if (res.success) setProjectActivityLogs(res.data || []);
    } catch (_) {
      // silently ignore
    } finally {
      setActivityLoading(false);
    }
  };

  const toCsv = (rows) =>
    rows
      .map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

  const downloadFile = (content, mimeType, fileName) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openPrintPreview = (title, headers, rows) => {
    const bodyRows = rows
      .map(
        (row) => `<tr>${row
          .map((value) => `<td>${String(value ?? '')}</td>`)
          .join('')}</tr>`
      )
      .join('');
    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    h2 { margin: 0 0 12px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ccc; padding: 8px; font-size: 12px; text-align: left; }
    th { background: #f3f4f6; }
  </style>
</head>
<body>
  <h2>${title}</h2>
  <table>
    <thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`;
    const win = window.open('', '_blank');
    if (!win) {
      toast.error('Unable to open export window');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  const handleFeatureExport = (type) => {
    setShowFeatureExportMenu(false);
    if (!featureRows.length) {
      toast.info('No features to export');
      return;
    }
    const headers = ['Feature ID', 'Feature Name', 'Status', 'Estimated Time', 'Actual Time', 'Description', 'Created By', 'Created At'];
    const rows = featureRows.map((feature) => [
      feature.featureCode || '',
      feature.name || '',
      feature.status || '',
      feature.totalEstimatedTime ?? 0,
      feature.totalActualTime ?? 0,
      feature.description || '',
      feature.createdBy?.name || '',
      feature.createdAt ? new Date(feature.createdAt).toISOString().slice(0, 10) : '',
    ]);
    const fileTag = `${project.projectCode || project.projectNo || project._id}-features`;
    const csv = toCsv([headers, ...rows]);
    if (type === 'csv') {
      downloadFile(`\uFEFFsep=,\n${csv}`, 'text/csv;charset=utf-8;', `${fileTag}.csv`);
      return;
    }
    if (type === 'excel') {
      openPrintPreview('Project Features Export', headers, rows);
      return;
    }
    if (type === 'pdf') {
      openPrintPreview('Project Features Export', headers, rows);
      toast.info('Use Save as PDF in print dialog');
      return;
    }
    openPrintPreview('Project Features Export', headers, rows);
  };

  const handleTaskExport = (type) => {
    setShowTaskExportMenu(false);
    if (!sortedTasks.length) {
      toast.info('No tasks to export');
      return;
    }
    const headers = ['Task ID', 'Task Name', 'Feature', 'Status', 'Start Date', 'Due Date', 'Assigned To', 'Priority'];
    const rows = sortedTasks.map((row) => [
      row.taskCode || (row._id ? `T${String(row._id).slice(-4).toUpperCase()}` : row.id || ''),
      row.name || '',
      row.featureId ? featureMap[String(row.featureId || '')] || '-' : '-',
      row.status || '-',
      row.startDate ? new Date(row.startDate).toISOString().slice(0, 10) : '-',
      row.dueDate ? new Date(row.dueDate).toISOString().slice(0, 10) : '-',
      row.assignedTo?.name || '-',
      row.priority || '-',
    ]);
    const fileTag = `${project.projectCode || project.projectNo || project._id}-tasks`;
    const csv = toCsv([headers, ...rows]);
    if (type === 'csv') {
      downloadFile(`\uFEFFsep=,\n${csv}`, 'text/csv;charset=utf-8;', `${fileTag}.csv`);
      return;
    }
    if (type === 'excel') {
      openPrintPreview('Project Tasks Export', headers, rows);
      return;
    }
    if (type === 'pdf') {
      openPrintPreview('Project Tasks Export', headers, rows);
      toast.info('Use Save as PDF in print dialog');
      return;
    }
    openPrintPreview('Project Tasks Export', headers, rows);
  };

  const handleUploadProjectFiles = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    const totalBytes = files.reduce((s, f) => s + (f.size || 0), 0);
    if (totalBytes > MAX_TOTAL_BYTES) {
      toast.error(`Total upload size is ${formatBytes(totalBytes)}. Max allowed is 500 MB.`);
      return;
    }
    const tooLarge = files.find((f) => (f.size || 0) > MAX_TOTAL_BYTES);
    if (tooLarge) {
      toast.error(`"${tooLarge.name}" exceeds the 500 MB limit.`);
      return;
    }
    try {
      setUploadingProjectFiles(true);
      await api.uploadProjectFiles(project._id, files);
      toast.success('Files uploaded');
      loadProject();
    } catch (error) {
      toast.error(error.message || 'Failed to upload files');
    } finally {
      setUploadingProjectFiles(false);
    }
  };

  const isImageFile = (file) => {
    const type = String(file?.fileType || '').toLowerCase();
    const name = String(file?.fileName || '').toLowerCase();
    return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
  };

  const handleAddFileComment = async () => {
    if (!selectedFile?._id) return;
    const text = fileCommentText.trim();
    if (!text) return;
    try {
      setSubmittingFileComment(true);
      await api.addProjectFileComment(project._id, selectedFile._id, text);
      setFileCommentText('');
      const refreshedProject = await loadProject();
      const updated = (refreshedProject?.files || []).find((file) => String(file._id) === String(selectedFile._id));
      if (updated) setSelectedFile(updated);
      toast.success('Comment added');
    } catch (error) {
      toast.error(error.message || 'Failed to add comment');
    } finally {
      setSubmittingFileComment(false);
    }
  };

  const handleUpdateFileComment = async () => {
    if (!selectedFile?._id || !editingFileCommentId) return;
    const text = editingFileCommentText.trim();
    if (!text) return;
    try {
      setSubmittingFileComment(true);
      await api.updateProjectFileComment(project._id, selectedFile._id, editingFileCommentId, text);
      const refreshedProject = await loadProject();
      const updated = (refreshedProject?.files || []).find((file) => String(file._id) === String(selectedFile._id));
      if (updated) setSelectedFile(updated);
      setEditingFileCommentId('');
      setEditingFileCommentText('');
      toast.success('Comment updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update comment');
    } finally {
      setSubmittingFileComment(false);
    }
  };

  const handleDeleteFileComment = async (commentId) => {
    if (!selectedFile?._id || !commentId) return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      setDeletingCommentId(commentId);
      await api.deleteProjectFileComment(project._id, selectedFile._id, commentId);
      const refreshedProject = await loadProject();
      const updated = (refreshedProject?.files || []).find((file) => String(file._id) === String(selectedFile._id));
      if (updated) setSelectedFile(updated);
      if (editingFileCommentId === commentId) {
        setEditingFileCommentId('');
        setEditingFileCommentText('');
      }
      toast.success('Comment deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete comment');
    } finally {
      setDeletingCommentId('');
    }
  };

  const handleDeleteProjectFile = async (fileId) => {
    if (!fileId) return;
    try {
      setDeletingProjectFileId(String(fileId));
      await api.deleteProjectFile(project._id, fileId);
      toast.success('File deleted');
      if (selectedFile && String(selectedFile._id) === String(fileId)) {
        setSelectedFile(null);
      }
      setConfirmDeleteProjectFileId('');
      await loadProject();
    } catch (error) {
      toast.error(error.message || 'Failed to delete file');
    } finally {
      setDeletingProjectFileId('');
    }
  };

  const handleDownloadAllProjectFiles = async () => {
    const files = project?.files || [];
    if (!files.length) {
      toast.info('No files available to download');
      return;
    }

    try {
      const { blob, fileName } = await api.downloadAllProjectFiles(project._id);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || `project-files-${project._id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      toast.success('Project files download started');
    } catch (error) {
      toast.error(error.message || 'Failed to download project files');
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <div style={cardStyle}>
        <div style={tabBarStyle}>
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                ...tabStyle,
                ...(activeTab === tab ? activeTabStyle : {})
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'Overview' && (
        <div style={overviewLayoutStyle}>
          <div style={overviewMainCardStyle}>
            <div style={overviewHeaderStyle}>
              <div style={overviewHeaderIconStyle}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 2 7 12 12 22 7 12 2" />
                  <polyline points="2 17 12 22 22 17" />
                  <polyline points="2 12 12 17 22 12" />
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={overviewEyebrowStyle}>
                  PROJECT {project.projectCode || (project.projectNo ? `P${String(project.projectNo).padStart(4, '0')}` : '')}
                </div>
                {!editingOverviewName ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={overviewTitleStyle}>{project.name || 'Untitled Project'}</div>
                    {canEditOverview && (
                      <button type="button" style={overviewEditBtnStyle} onClick={() => setEditingOverviewName(true)}>
                        Edit
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <input
                      value={overviewNameDraft}
                      onChange={(e) => setOverviewNameDraft(e.target.value)}
                      placeholder="Project name"
                      style={{ ...searchInputStyle, width: '260px', minWidth: 'unset' }}
                    />
                    <button
                      type="button"
                      disabled={savingOverview}
                      style={miniLinkBtnStyle}
                      onClick={async () => {
                        const trimmed = (overviewNameDraft || '').trim();
                        if (!trimmed) {
                          toast.warning('Project name is required');
                          return;
                        }
                        const ok = await saveOverviewField({ name: trimmed }, 'Project name updated');
                        if (ok) setEditingOverviewName(false);
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      style={miniLinkBtnStyle}
                      onClick={() => {
                        setEditingOverviewName(false);
                        setOverviewNameDraft(project.name || '');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <span style={getStatusPillStyle(project.status)}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: getStatusPalette(project.status).dot }} />
                {getProjectStatusLabel(project.status)}
              </span>
            </div>

            <h3 style={overviewSectionTitleStyle}>OVERVIEW</h3>
            <div style={infoRowStyle}><span>Project #</span><strong>{project.projectCode || (project.projectNo ? `P${String(project.projectNo).padStart(4, '0')}` : '-')}</strong></div>
              {/* <div style={infoRowStyle}><span>Customer</span><strong>{project.customer}</strong></div> */}
              <div style={infoRowStyle}>
                <span>Project Leader</span>
                {!editingOverviewTeamLeader ? (
                  <strong style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{project.teamLeader?.name || '-'}</span>
                    {canEditOverview && (
                      <button
                        type="button"
                        style={overviewEditBtnStyle}
                        onClick={() => setEditingOverviewTeamLeader(true)}
                      >
                        Edit
                      </button>
                    )}
                  </strong>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <select
                      value={overviewTeamLeaderDraft}
                      onChange={(e) => setOverviewTeamLeaderDraft(e.target.value)}
                      style={{ ...searchInputStyle, width: '170px', minWidth: 'unset' }}
                    >
                      <option value="">No project leader</option>
                      {sortUsersByRole(users).map((user) => (
                        <option key={user._id || user.id} value={user._id || user.id}>
                          {user.name || 'Unnamed User'} ({user.role || '-'})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={savingOverview}
                      style={miniLinkBtnStyle}
                      onClick={async () => {
                        // Changing the project leader clears ALL existing members
                        // (old leader + everyone else). Only the newly selected leader
                        // remains as a member — user re-adds the rest under the new
                        // leader's hierarchy afterwards.
                        const members = overviewTeamLeaderDraft ? [overviewTeamLeaderDraft] : [];
                        const ok = await saveOverviewField(
                          { teamLeader: overviewTeamLeaderDraft || null, members },
                          'Project leader updated'
                        );
                        if (ok) setEditingOverviewTeamLeader(false);
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      style={miniLinkBtnStyle}
                      onClick={() => {
                        setEditingOverviewTeamLeader(false);
                        setOverviewTeamLeaderDraft(project.teamLeader?._id || project.teamLeader?.id || '');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <div style={infoRowStyle}>
                <span>Status</span>
                {!editingOverviewStatus ? (
                  <strong style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{getProjectStatusLabel(project.status)}</span>
                    {canEditOverview && (
                      <button type="button" style={overviewEditBtnStyle} onClick={() => setEditingOverviewStatus(true)}>
                        Edit
                      </button>
                    )}
                  </strong>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <select
                      value={overviewStatusDraft}
                      onChange={(e) => setOverviewStatusDraft(e.target.value)}
                      style={{ ...searchInputStyle, width: '160px', minWidth: 'unset' }}
                    >
                      {[
                        { value: 'Not Started', label: 'Not Started' },
                        { value: 'In Progress', label: 'In Progress' },
                        { value: 'On Hold', label: 'On Hold' },
                        { value: 'Cancelled', label: 'Cancelled' },
                        { value: 'Finished', label: 'Completed' },
                      ].map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={savingOverview}
                      style={miniLinkBtnStyle}
                      onClick={async () => {
                        const ok = await saveOverviewField({ status: overviewStatusDraft }, 'Status updated');
                        if (ok) setEditingOverviewStatus(false);
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      style={miniLinkBtnStyle}
                      onClick={() => {
                        setEditingOverviewStatus(false);
                        setOverviewStatusDraft(project.status || 'Not Started');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              {/* <div style={infoRowStyle}><span>Total Estimated Effort</span><strong>{project.totalEstimatedEffort ?? 0}</strong></div> */}
              {/* <div style={infoRowStyle}><span>Total Actual Effort</span><strong>{project.totalActualEffort ?? 0}</strong></div> */}
             
              <div style={infoRowStyle}>
                <span>Start Date</span>
                {!editingOverviewStartDate ? (
                  <strong style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{project.startDate ? new Date(project.startDate).toISOString().slice(0, 10) : '-'}</span>
                    {canEditOverview && (
                      <button type="button" style={overviewEditBtnStyle} onClick={() => setEditingOverviewStartDate(true)}>
                        Edit
                      </button>
                    )}
                  </strong>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <input
                      type="date"
                      max={project.deadline ? new Date(project.deadline).toISOString().slice(0, 10) : undefined}
                      value={overviewStartDateDraft}
                      onChange={(e) => setOverviewStartDateDraft(e.target.value)}
                      style={{ ...searchInputStyle, width: '150px', minWidth: 'unset' }}
                    />
                    <button
                      type="button"
                      disabled={savingOverview}
                      style={miniLinkBtnStyle}
                      onClick={async () => {
                        const trimmed = (overviewStartDateDraft || '').trim();
                        if (!trimmed) {
                          toast.warning('Start date is required');
                          return;
                        }
                        const deadline = project.deadline ? new Date(project.deadline) : null;
                        const nextStart = new Date(trimmed);
                        if (deadline && !Number.isNaN(deadline.getTime()) && !Number.isNaN(nextStart.getTime())) {
                          const s = new Date(nextStart);
                          s.setHours(0, 0, 0, 0);
                          const d = new Date(deadline);
                          d.setHours(0, 0, 0, 0);
                          if (s > d) {
                            toast.warning('Start date cannot be after the deadline');
                            return;
                          }
                        }
                        const ok = await saveOverviewField({ startDate: trimmed }, 'Start date updated');
                        if (ok) setEditingOverviewStartDate(false);
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      style={miniLinkBtnStyle}
                      onClick={() => {
                        setEditingOverviewStartDate(false);
                        setOverviewStartDateDraft(
                          project.startDate ? new Date(project.startDate).toISOString().slice(0, 10) : ''
                        );
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <div style={infoRowStyle}>
                <span>Deadline</span>
                {!editingOverviewDeadline ? (
                  <strong style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{project.deadline ? new Date(project.deadline).toISOString().slice(0, 10) : '-'}</span>
                    {canEditOverview && (
                      <button type="button" style={overviewEditBtnStyle} onClick={() => setEditingOverviewDeadline(true)}>
                        Edit
                      </button>
                    )}
                  </strong>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <input
                      type="date"
                      min={project.startDate ? new Date(project.startDate).toISOString().slice(0, 10) : undefined}
                      value={overviewDeadlineDraft}
                      onChange={(e) => setOverviewDeadlineDraft(e.target.value)}
                      style={{ ...searchInputStyle, width: '150px', minWidth: 'unset' }}
                    />
                    <button
                      type="button"
                      disabled={savingOverview}
                      style={miniLinkBtnStyle}
                      onClick={async () => {
                        const trimmed = (overviewDeadlineDraft || '').trim();
                        if (!trimmed) {
                          toast.warning('Deadline is required');
                          return;
                        }
                        const start = project.startDate ? new Date(project.startDate) : null;
                        const nextDeadline = new Date(trimmed);
                        if (start && !Number.isNaN(start.getTime()) && !Number.isNaN(nextDeadline.getTime())) {
                          const s = new Date(start);
                          s.setHours(0, 0, 0, 0);
                          const d = new Date(nextDeadline);
                          d.setHours(0, 0, 0, 0);
                          if (d < s) {
                            toast.warning('Deadline cannot be before the start date');
                            return;
                          }
                        }
                        const ok = await saveOverviewField({ deadline: trimmed }, 'Deadline updated');
                        if (ok) setEditingOverviewDeadline(false);
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      style={miniLinkBtnStyle}
                      onClick={() => {
                        setEditingOverviewDeadline(false);
                        setOverviewDeadlineDraft(
                          project.deadline ? new Date(project.deadline).toISOString().slice(0, 10) : ''
                        );
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
               <div style={infoRowStyle}><span>Date Created</span><strong>{project.createdAt ? new Date(project.createdAt).toISOString().slice(0, 10) : '-'}</strong></div>

              <h3 style={{ ...overviewSectionTitleStyle, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>DESCRIPTION</span>
                {canEditOverview && !editingOverviewDescription && (
                  <button type="button" style={overviewEditBtnStyle} onClick={() => setEditingOverviewDescription(true)}>
                    Edit
                  </button>
                )}
              </h3>
              {!editingOverviewDescription ? (
                <div style={overviewDescriptionStyle}>{project.description || 'No description provided.'}</div>
              ) : (
                <div>
                  <textarea
                    value={overviewDescriptionDraft}
                    onChange={(e) => setOverviewDescriptionDraft(e.target.value)}
                    placeholder="Description"
                    style={{ ...searchInputStyle, width: '100%', minHeight: '90px', resize: 'vertical', padding: '10px 12px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      type="button"
                      disabled={savingOverview}
                      style={miniLinkBtnStyle}
                      onClick={async () => {
                        const ok = await saveOverviewField(
                          { description: overviewDescriptionDraft },
                          'Description updated'
                        );
                        if (ok) setEditingOverviewDescription(false);
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      style={miniLinkBtnStyle}
                      onClick={() => {
                        setEditingOverviewDescription(false);
                        setOverviewDescriptionDraft(project.description || '');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div style={peopleGridStyle}>
                <div style={peopleCardStyle}>
                  <div style={peopleCardTitleStyle}>
                    <span>MEMBERS</span>
                    {canEditOverview && !editingOverviewMembers && (
                  <button
                    type="button"
                    style={overviewEditBtnStyle}
                    onClick={() => {
                      setOverviewMemberSource('hierarchy');
                      setEditingOverviewMembers(true);
                    }}
                  >
                    Edit Members
                  </button>
                )}
              </div>
              {!editingOverviewMembers ? (
                <div>
                  {(project.members || []).length === 0 ? (
                    <div style={peopleEmptyStyle}>No members yet.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: '4px' }}>
                      {(showAllMembers ? (project.members || []) : (project.members || []).slice(0, 5)).map((member) => {
                        const memberId = String(member._id || member.id || '');
                        const leaderId = String(project.teamLeader?._id || project.teamLeader?.id || '');
                        const isLeader = memberId && memberId === leaderId;
                        return (
                          <div key={member._id || member.id} style={peopleRowStyle}>
                            <div style={memberAvatarStyle}>
                              {getInitials(member.name)}
                            </div>
                            <span style={peopleNameStyle}>{member.name}</span>
                            {isLeader && <span style={leaderBadgeStyle}>Project Leader</span>}
                          </div>
                        );
                      })}
                      {(project.members || []).length > 5 && (
                        <button type="button" style={showMoreLinkStyle} onClick={() => setShowAllMembers((prev) => !prev)}>
                          {showAllMembers ? 'Show less' : `+${(project.members || []).length - 5} more`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <label style={memberSourceRadioStyle}>
                      <input
                        type="radio"
                        name="overviewMemberSource"
                        checked={overviewMemberSource === 'hierarchy'}
                        onChange={() => setOverviewMemberSource('hierarchy')}
                      />
                      Hierarchy
                    </label>
                    <label style={memberSourceRadioStyle}>
                      <input
                        type="radio"
                        name="overviewMemberSource"
                        checked={overviewMemberSource === 'all'}
                        onChange={() => setOverviewMemberSource('all')}
                      />
                      All Users
                    </label>
                  </div>
                  <UserMultiSelectDropdown
                    label="Members"
                    placeholder={
                      overviewMemberSource === 'all' || overviewTeamLeaderDraft
                        ? 'Select members'
                        : 'Select a project leader first'
                    }
                    users={memberOptionUsers}
                    selectedIds={overviewMembersDraft}
                    onChange={setOverviewMembersDraft}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      type="button"
                      disabled={savingOverview}
                      style={miniLinkBtnStyle}
                      onClick={async () => {
                        const members = overviewTeamLeaderDraft && !overviewMembersDraft.includes(overviewTeamLeaderDraft)
                          ? [...overviewMembersDraft, overviewTeamLeaderDraft]
                          : overviewMembersDraft;
                        const ok = await saveOverviewField({ members }, 'Members updated');
                        if (ok) setEditingOverviewMembers(false);
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      style={miniLinkBtnStyle}
                      onClick={() => {
                        setEditingOverviewMembers(false);
                        setOverviewMembersDraft((project.members || []).map((member) => member._id || member.id).filter(Boolean));
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

                </div>
                <div style={peopleCardStyle}>
                  <div style={peopleCardTitleStyle}>
                    <span>FOLLOWERS</span>
                    {canEditOverview && !editingOverviewFollowers && (
                      <button
                        type="button"
                        style={overviewEditBtnStyle}
                        onClick={() => setEditingOverviewFollowers(true)}
                      >
                        Edit Followers
                      </button>
                    )}
                  </div>
              {!editingOverviewFollowers ? (
                <div>
                  {(project.followers || []).length === 0 ? (
                    <div style={peopleEmptyStyle}>No followers yet.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: '4px' }}>
                      {(showAllFollowers ? (project.followers || []) : (project.followers || []).slice(0, 5)).map((follower) => (
                        <div key={follower._id || follower.id} style={peopleRowStyle}>
                          <div style={memberAvatarStyle}>
                            {getInitials(follower.name)}
                          </div>
                          <span style={peopleNameStyle}>{follower.name}</span>
                        </div>
                      ))}
                      {(project.followers || []).length > 5 && (
                        <button type="button" style={showMoreLinkStyle} onClick={() => setShowAllFollowers((prev) => !prev)}>
                          {showAllFollowers ? 'Show less' : `+${(project.followers || []).length - 5} more`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <UserMultiSelectDropdown
                    label="Followers"
                    placeholder="Select followers"
                    users={users}
                    selectedIds={overviewFollowersDraft}
                    onChange={setOverviewFollowersDraft}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      type="button"
                      disabled={savingOverview}
                      style={miniLinkBtnStyle}
                      onClick={async () => {
                        const ok = await saveOverviewField({ followers: overviewFollowersDraft }, 'Followers updated');
                        if (ok) setEditingOverviewFollowers(false);
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      style={miniLinkBtnStyle}
                      onClick={() => {
                        setEditingOverviewFollowers(false);
                        setOverviewFollowersDraft((project.followers || []).map((follower) => follower._id || follower.id).filter(Boolean));
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
                </div>
              </div>
            </div>

            <div style={overviewSidebarStyle}>
              <div style={statCardsRowStyle}>
                <div style={{ ...statCardStyle, background: '#f0fff4', borderColor: '#c6f6d5' }}>
                  <div style={{ ...statIconStyle, background: '#c6f6d5', color: '#276749' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <div style={statValueStyle}>{overviewOpenTasks.open}</div>
                  <div style={statLabelStyle}>
                    Open Tasks <span style={{ color: '#a0aec0' }}>/ {overviewOpenTasks.total}</span>
                  </div>
                  <div style={{ ...progressTrackStyle, marginTop: '10px' }}>
                    <div style={{ ...progressFillStyle, width: `${overviewOpenTasks.barPct}%` }} />
                  </div>
                </div>

                <div style={{ ...statCardStyle, background: '#fff5f5', borderColor: '#fed7d7' }}>
                  <div style={{ ...statIconStyle, background: '#fed7d7', color: '#c53030' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <polyline points="12 7 12 12 15 14" />
                    </svg>
                  </div>
                  <div style={{ ...statValueStyle, color: isOverviewDeadlineMissed ? '#c53030' : statValueStyle.color }}>
                    {overviewTimeline.hasDates ? overviewTimeline.remainingDays : '—'}
                  </div>
                  <div style={statLabelStyle}>
                    {overviewTimeline.hasDates
                      ? `Day${overviewTimeline.remainingDays === 1 ? '' : 's'} Left`
                      : 'Set start & deadline'}
                  </div>
                  <div style={{ ...progressTrackStyle, marginTop: '10px' }}>
                    <div
                      style={{
                        ...progressFillStyle,
                        width: `${overviewTimeline.hasDates ? overviewTimeline.scheduleUsedPct : 0}%`,
                        background: isOverviewDeadlineMissed ? '#e53e3e' : progressFillStyle.background,
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={progressCardStyle}>
                <div style={progressCardTitleStyle}>Project Progress</div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                  <div
                    style={{
                      ...progressCircleOuter,
                      background: `conic-gradient(#48bb78 0deg, #48bb78 ${(overviewDisplayProgress / 100) * 360}deg, #edf2f7 ${(overviewDisplayProgress / 100) * 360}deg, #edf2f7 360deg)`,
                    }}
                  >
                    <div style={progressCircleInner}>{overviewProgressPctLabel}</div>
                  </div>
                </div>
                <div style={totalProgressBoxStyle}>
                  <div style={{ fontSize: '20px', fontWeight: 800, color: '#2f855a' }}>{overviewProgressPctLabel}</div>
                  <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>Total Progress</div>
                </div>
              </div>
            </div>
          </div>
      )}

      {activeTab === 'Features' && (
        <div style={{ ...cardStyle, marginTop: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '16px' }}>
            {[
              { label: 'Planned', value: featureSummary.Planned, color: '#4a5568' },
              { label: 'In Progress', value: featureSummary['In Progress'], color: '#2b6cb0' },
              { label: 'Completed', value: featureSummary.Completed, color: '#2f855a' },
            ].map((item) => (
              <div
                key={item.label}
                onClick={() => setFeatureStatusCardFilter((prev) => (prev === item.label ? 'All' : item.label))}
                style={{
                  ...taskSummaryCardStyle,
                  cursor: 'pointer',
                  border: featureStatusCardFilter === item.label ? '2px solid #4c51bf' : '1px solid #e2e8f0',
                }}
              >
                <div style={{ fontSize: '34px', lineHeight: 1, color: '#1a202c', fontWeight: 700 }}>{item.value || 0}</div>
                <div style={{ color: item.color, fontSize: '12px', fontWeight: 600, marginTop: '6px' }}>{item.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <select
                value={featureStatusCardFilter}
                onChange={(e) => setFeatureStatusCardFilter(e.target.value)}
                style={{ ...searchInputStyle, minWidth: '160px' }}
              >
                <option value="All">All Status</option>
                {FEATURE_STATUSES.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <input
                value={searchFeature}
                onChange={(e) => setSearchFeature(e.target.value)}
                placeholder="Search..."
                style={searchInputStyle}
              />
              <button
                type="button"
                disabled={!hasActiveFeatureFilters}
                title={hasActiveFeatureFilters ? 'Clear all filters' : 'No filters applied'}
                style={hasActiveFeatureFilters ? resetBtnActiveStyle : resetBtnDisabledStyle}
                onClick={() => {
                  setFeatureStatusCardFilter('All');
                  setSearchFeature('');
                }}
              >
                RESET
              </button>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {canEditProjectPerm && (
              <button
                type="button"
                style={{ ...toolbarBtnStyle, background: '#edf2ff', color: '#2b6cb0', borderColor: '#c3dafe' }}
                onClick={() => {
                  setEditingFeatureId('');
                  setNewFeature({ name: '', description: '', totalEstimatedTime: '', totalActualTime: '', status: 'Planned' });
                  setShowAddFeatureModal(true);
                }}
              >
                + ADD FEATURE
              </button>
              )}
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  style={toolbarBtnStyle}
                  onClick={() => setShowFeatureExportMenu((prev) => !prev)}
                >
                  EXPORT
                </button>
                {showFeatureExportMenu && (
                  <div style={exportMenuStyle}>
                    {[
                      ['Excel', 'excel'],
                      ['CSV', 'csv'],
                      ['PDF', 'pdf'],
                      ['Print', 'print'],
                    ].map(([label, value]) => (
                      <button key={value} type="button" style={exportMenuItemStyle} onClick={() => handleFeatureExport(value)}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '13px', fontWeight: 700, color: '#2d3748' }}>
              Features / Epics
            </div>
            {filteredFeatureRows.length === 0 ? (
              <div style={{ padding: '12px', color: '#718096', fontSize: '13px' }}>No feature found for selected filters.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      {['Feature ID', 'Feature Name', 'Status', 'Estimated Time', 'Actual Time', 'Description', 'Created By', 'Created At', 'Actions'].map((label) => (
                        <th key={label} style={tableHeadStyle}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFeatureRows.map((feature) => (
                      <tr key={feature._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={tableCellStyle}>
                          <button
                            type="button"
                            onClick={() => navigate(projectManagementPaths.featureView(project._id, feature._id))}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              margin: 0,
                              color: '#2b6cb0',
                              fontWeight: 700,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                            title="Open feature details"
                          >
                            {feature.featureCode || '-'}
                          </button>
                        </td>
                        <td style={tableCellStyle}>{feature.name || '-'}</td>
                        <td style={tableCellStyle}>{feature.status || '-'}</td>
                        <td style={tableCellStyle}>{feature.totalEstimatedTime ?? 0}</td>
                        <td style={tableCellStyle}>{feature.totalActualTime ?? 0}</td>
                        <td style={tableCellStyle}>{feature.description || '-'}</td>
                        <td style={tableCellStyle}>{feature.createdBy?.name || '-'}</td>
                        <td style={tableCellStyle}>{feature.createdAt ? new Date(feature.createdAt).toISOString().slice(0, 10) : '-'}</td>
                        <td style={tableCellStyle}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {canAddTaskPerm && (
                            <button
                              type="button"
                              style={{ ...toolbarBtnStyle, height: '28px', padding: '0 8px', background: '#ebf8ff', borderColor: '#bee3f8', color: '#2b6cb0' }}
                              onClick={() => handleCreateTaskFromFeature(feature)}
                              title="Create task from feature"
                            >
                              +Task
                            </button>
                            )}
                            {canEditProjectPerm && (
                            <button
                              type="button"
                              style={{ ...toolbarBtnStyle, height: '28px', padding: '0 8px' }}
                              onClick={() => handleEditFeature(feature)}
                            >
                              ✏️
                            </button>
                            )}
                            {canEditProjectPerm && (
                              <button
                                type="button"
                                style={{ ...toolbarBtnStyle, height: '28px', padding: '0 8px', background: '#fff5f5', borderColor: '#fed7d7', color: '#c53030' }}
                                onClick={() => setConfirmDeleteFeatureId(feature._id)}
                                disabled={deletingFeatureId === feature._id}
                              >
                                {deletingFeatureId === feature._id ? '…' : '🗑️'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'Tasks' && (
        <div style={{ ...cardStyle, marginTop: '12px' }}>
          <div className="pj-stat-grid">
            {/* All Status — total tasks (first card). */}
            <div
              onClick={() => {
                setTaskOverdueOnly(false);
                setTaskStatusCardFilter([]);
              }}
              className={`pj-stat-card${taskStatusCardFilter.length === 0 && !taskOverdueOnly ? ' is-active' : ''}`}
              style={{ '--accent': '#475569' }}
            >
              <div className="pj-stat-top">
                <span className="pj-stat-label">All Status</span>
              </div>
              <div className="pj-stat-value">{taskRows.length}</div>
            </div>
            {[
              { label: 'Not Started', value: taskSummary['Not Started'], color: '#64748b' },
              { label: 'In Progress', value: taskSummary['In Progress'], color: '#3b82f6' },
              { label: 'R&D Development', value: taskSummary['R&D Development'], color: '#0891b2' },
              { label: 'R&D Testing', value: taskSummary['R&D Testing'], color: '#6366f1' },
              { label: 'Final Verification', value: taskSummary['Final Verification'], color: '#ec4899' },
              { label: 'Completed', status: 'Complete', value: taskSummary.Complete, color: '#10b981' },
              { label: 'Re-open', value: taskSummary['Re-open'], color: '#ef4444' },
              { label: 'On Hold', value: taskSummary['On Hold'], color: '#f59e0b' },
              { label: 'Invalid Issue', value: taskSummary['Invalid Issue'], color: '#9333ea' }
            ].map((item) => {
              const statusKey = item.status || item.label;
              const active = taskStatusCardFilter.includes(statusKey) && !taskOverdueOnly;
              const toggleStatus = () => {
                setTaskOverdueOnly(false);
                setTaskStatusCardFilter((prev) => (prev.includes(statusKey) ? prev.filter((s) => s !== statusKey) : [...prev, statusKey]));
              };
              return (
                <div
                  key={item.label}
                  onClick={(e) => {
                    if (e.target.type === 'checkbox') return;
                    toggleStatus();
                  }}
                  className={`pj-stat-card${active ? ' is-active' : ''}`}
                  style={{ '--accent': item.color }}
                >
                  <div className="pj-stat-top" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleStatus();
                      }}
                      style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: item.color, flexShrink: 0 }}
                    />
                    <span className="pj-stat-label">{item.label}</span>
                  </div>
                  <div className="pj-stat-value">{item.value}</div>
                </div>
              );
            })}
            <div
              onClick={() => {
                setTaskOverdueOnly((prev) => !prev);
                setTaskStatusCardFilter([]);
              }}
              className={`pj-stat-card${taskOverdueOnly ? ' is-active' : ''}`}
              style={{ '--accent': '#dc2626' }}
            >
              <div className="pj-stat-top">
                <span className="pj-stat-label">Overdue</span>
              </div>
              <div className="pj-stat-value">{overdueTaskCount}</div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <input
                value={searchTask}
                onChange={(e) => setSearchTask(e.target.value)}
                placeholder="Search..."
                style={searchInputStyle}
              />
              <button
                type="button"
                disabled={!hasActiveTaskFilters}
                title={hasActiveTaskFilters ? 'Clear all filters' : 'No filters applied'}
                onClick={() => {
                  setSearchTask('');
                  setTaskFeatureFilter('All');
                  setTaskStatusCardFilter([]);
                  setTaskOverdueOnly(false);
                }}
                style={hasActiveTaskFilters ? resetBtnActiveStyle : resetBtnDisabledStyle}
              >
                RESET
              </button>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {canAddTaskPerm && (
              <button
                type="button"
                style={{ ...toolbarBtnStyle, border: 'none', color: 'white', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                onClick={() => {
                  setEditingTaskId('');
                  setNewTask({
                    name: '',
                    status: 'Not Started',
                    priority: 'Medium',
                    taskCategory: 'Requirement',
                    public: false,
                    billable: false,
                    startDate: '',
                    dueDate: '',
                    featureId: '',
                    assignees: currentUserId && projectMemberIds.includes(String(currentUserId)) ? [currentUserId] : [],
                    followers: projectFollowerIds,
                    hour: '',
                    actualHour: '',
                    taskDescription: '',
                    comment: '',
                  });
                  setPendingAttachments([]);
                  setExistingAttachments([]);
                  setShowAddTaskModal(true);
                }}
              >
                + ADD TASK
              </button>
              )}
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  style={toolbarBtnStyle}
                  onClick={() => setShowTaskExportMenu((prev) => !prev)}
                >
                  EXPORT
                </button>
                {showTaskExportMenu && (
                  <div style={exportMenuStyle}>
                    {[
                      ['Excel', 'excel'],
                      ['CSV', 'csv'],
                      ['PDF', 'pdf'],
                      ['Print', 'print'],
                    ].map(([label, value]) => (
                      <button key={value} type="button" style={exportMenuItemStyle} onClick={() => handleTaskExport(value)}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '720px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '8%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '11%' }} />
              </colgroup>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  {[
                    ['taskId', 'Task ID'],
                    ['name', 'Task Name'],
                    ['assignee', 'Assigned To'],
                    ['priority', 'Priority'],
                    ['status', 'Status'],
                    ['startDate', 'Start Date'],
                    ['dueDate', 'Due Date'],
                  ].map(([field, label]) => (
                    <SortableTh
                      key={field}
                      field={field}
                      label={label}
                      sort={taskSort}
                      onSort={(f) => setTaskSort((prev) => nextSort(prev, f))}
                      style={tableHeadStyle}
                      variant="light"
                    />
                  ))}
                  <th style={{ ...tableHeadStyle, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTasks.map((row) => (
                  <tr key={row._id || row.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={tableCellStyle}>
                      <button
                        type="button"
                        onClick={() => navigate(projectManagementPaths.taskView(row._id || row.id))}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          margin: 0,
                          color: '#2b6cb0',
                          fontWeight: 700,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                        title="Open task details"
                      >
                        {row.taskCode || (row._id ? `T${String(row._id).slice(-4).toUpperCase()}` : row.id)}
                      </button>
                    </td>
                    <td style={tableCellStyle}>
                      {row.name || '-'}
                      {(row.attachments || []).length > 0 && (
                        <span
                          title={`${row.attachments.length} attachment${row.attachments.length > 1 ? 's' : ''}`}
                          style={{ marginLeft: '6px', cursor: 'default' }}
                        ><MdAttachFile size={15} color="#1a202c" style={{ verticalAlign: '-3px' }} /></span>
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      {(() => {
                        const assignees = Array.isArray(row.assignees) ? row.assignees : [];
                        const assigneeNames = assignees
                          .map((assignee) => {
                            if (assignee && typeof assignee === 'object') {
                              return assignee.name || assignee.email || '';
                            }
                            const userId = String(assignee || '').trim();
                            if (!userId) return '';
                            const user = users.find((u) => String(u._id || u.id) === userId);
                            return user?.name || user?.email || '';
                          })
                          .filter(Boolean);
                        if (!assigneeNames.length && row.assignedTo?.name) {
                          assigneeNames.push(row.assignedTo.name);
                        }
                        if (!assigneeNames.length) return '-';
                        const overflowAssignees = assigneeNames.length > 3;
                        const shownAssignees = overflowAssignees ? assigneeNames.slice(0, 2) : assigneeNames;
                        const extraAssignees = overflowAssignees ? assigneeNames.slice(2) : [];
                        return (
                          <div
                            style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}
                            title={assigneeNames.join(', ')}
                          >
                            {shownAssignees.map((name, idx) => (
                              <div key={`${row._id || row.id}-assignee-${idx}`} title={name} style={memberAvatarStyle}>
                                {getInitials(name)}
                              </div>
                            ))}
                            {extraAssignees.length > 0 && (
                              <div title={extraAssignees.join(', ')} style={memberAvatarStyle}>
                                +{extraAssignees.length}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={tableCellStyle}>
                      <select
                        value={row.priority}
                        disabled={!canEditTask(row) || statusUpdatingTaskId === String(row._id || row.id)}
                        onChange={(event) => updateTaskPriorityFromTable(row, event.target.value)}
                        style={{
                          width: '100%',
                          height: '30px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          padding: '0 6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: row.priority === 'High' ? '#dd6b20' : row.priority === 'Medium' ? '#2b6cb0' : '#2f855a',
                          background: '#fff',
                          cursor: !canEditTask(row) || statusUpdatingTaskId === String(row._id || row.id) ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {PRIORITIES.map((priority) => (
                          <option key={priority} value={priority}>{priority}</option>
                        ))}
                      </select>
                    </td>
                    <td style={tableCellStyle}>
                      <select
                        value={row.status}
                        disabled={!canEditTask(row) || statusUpdatingTaskId === String(row._id || row.id)}
                        onChange={(event) => updateTaskStatusFromTable(row, event.target.value)}
                        style={{
                          width: '100%',
                          height: '30px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          padding: '0 6px',
                          fontSize: '12px',
                          color: '#2d3748',
                          background: '#fff',
                          cursor: !canEditTask(row) || statusUpdatingTaskId === String(row._id || row.id) ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {TASK_STATUSES.map((status) => (
                          <option key={status} value={status}>{STATUS_DISPLAY_LABEL[status] || status}</option>
                        ))}
                      </select>
                    </td>
                    <td style={tableCellStyle}>{row.startDate ? new Date(row.startDate).toISOString().slice(0, 10) : '-'}</td>
                    <td style={tableCellStyle}>{row.dueDate ? new Date(row.dueDate).toISOString().slice(0, 10) : '-'}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                        {canEditTask(row) && (
                          <button
                            type="button"
                            style={{ ...toolbarBtnStyle, height: '28px', padding: '0 8px' }}
                            onClick={() => handleEditTask(row)}
                            title="Edit task"
                          >
                            ✏️
                          </button>
                        )}
                        {canEditTask(row) && (
                          <button
                            type="button"
                            style={{ ...toolbarBtnStyle, height: '28px', padding: '0 8px', background: '#eef2ff', borderColor: '#c3dafe', color: '#4c51bf' }}
                            onClick={() => handleSendReminder(row)}
                            disabled={remindingTaskId === String(row._id || row.id)}
                            title="Send reminder to assignees (in-app + email)"
                          >
                            {remindingTaskId === String(row._id || row.id) ? '…' : '🔔'}
                          </button>
                        )}
                        {canDeleteTaskPerm && (
                          <button
                            type="button"
                            style={{ ...toolbarBtnStyle, height: '28px', padding: '0 8px', background: '#fff5f5', borderColor: '#fed7d7', color: '#c53030' }}
                            onClick={() => setConfirmDeleteTaskId(String(row._id || row.id))}
                            disabled={deletingTaskId === String(row._id || row.id)}
                            title="Delete task"
                          >
                            {deletingTaskId === String(row._id || row.id) ? '…' : '🗑️'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={taskCurrentPage}
            totalPages={taskTotalPages}
            totalItems={filteredTasks.length}
            itemsPerPage={taskItemsPerPage}
            onPageChange={setTaskCurrentPage}
            onItemsPerPageChange={(value) => {
              setTaskItemsPerPage(value);
              setTaskCurrentPage(1);
            }}
          />
        </div>
      )}

      {activeTab === 'Files' && (
        <div style={{ ...cardStyle, marginTop: '12px' }}>
          {canEditProjectPerm && (
          <div
            style={{
              minHeight: '120px',
              border: '1px dashed #cbd5e0',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#2b6cb0',
              fontWeight: 600,
              marginBottom: '12px'
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              handleUploadProjectFiles(event.dataTransfer.files);
            }}
          >
            <label style={{ cursor: 'pointer' }}>
              {uploadingProjectFiles ? 'Uploading...' : 'Drop files here or click to upload'}
              <input
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(event) => handleUploadProjectFiles(event.target.files)}
              />
            </label>
          </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" style={toolbarBtnStyle} onClick={handleDownloadAllProjectFiles}>DOWNLOAD ALL</button>
            </div>
            <input
              value={searchFile}
              onChange={(e) => setSearchFile(e.target.value)}
              placeholder="Search..."
              style={searchInputStyle}
            />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '980px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  {['Filename', 'File type', 'Last Activity', 'Total Comments', 'Visible to Customer', 'Uploaded by', 'Date uploaded', 'Options'].map((label) => (
                    <th key={label} style={tableHeadStyle}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((row) => (
                  <tr key={row._id || row.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={tableCellStyle}>
                      <div>{row.fileName}</div>
                      {row.expiresAt && (
                        <div style={{ fontSize: '11px', color: '#a0aec0', marginTop: '2px' }}>
                          Deletes on {formatExpiryDate(row.expiresAt)}
                        </div>
                      )}
                    </td>
                    <td style={tableCellStyle}>{row.fileType}</td>
                    <td style={tableCellStyle}>{row.lastActivity}</td>
                    <td style={tableCellStyle}>{row.totalComments}</td>
                    <td style={tableCellStyle}>{row.visibleToCustomer ? 'Yes' : 'No'}</td>
                    <td style={tableCellStyle}>{row.uploadedBy?.name || '-'}</td>
                    <td style={tableCellStyle}>{row.createdAt ? new Date(row.createdAt).toISOString().slice(0, 10) : '-'}</td>
                    <td style={tableCellStyle}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          style={{ ...toolbarBtnStyle, height: '28px' }}
                          onClick={() => setSelectedFile(row)}
                          title="View file"
                        >
                          👁
                        </button>
                        {canEditProjectPerm && (
                        <button
                          type="button"
                          style={{ ...toolbarBtnStyle, height: '28px', background: '#fff5f5', borderColor: '#fed7d7', color: '#c53030' }}
                          onClick={() => setConfirmDeleteProjectFileId(String(row._id || row.id))}
                          disabled={deletingProjectFileId === String(row._id || row.id)}
                        >
                          {deletingProjectFileId === String(row._id || row.id) ? '…' : '🗑️'}
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'Notes' && (
        <div style={{ ...cardStyle, marginTop: '12px' }}>
          <div style={{ fontWeight: 600, color: '#2d3748', marginBottom: '10px' }}>Project notes</div>
          <textarea
            value={noteInput}
            onChange={(event) => setNoteInput(event.target.value)}
            placeholder="Add a new note..."
            style={{
              width: '100%',
              minHeight: '120px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '12px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
          <div style={{ marginTop: '10px' }}>
            <button
              type="button"
              onClick={async () => {
                const text = noteInput.trim();
                if (!text) {
                  toast.warning('Note cannot be empty');
                  return;
                }
                try {
                  setSavingNote(true);
                  await api.updateProjectNotes(project._id, text);
                  setNoteInput('');
                  await loadProject();
                  toast.success('Note added');
                } catch (error) {
                  toast.error(error.message || 'Failed to add note');
                } finally {
                  setSavingNote(false);
                }
              }}
              style={{ ...toolbarBtnStyle, background: '#3182ce', color: 'white', borderColor: '#3182ce' }}
            >
              {savingNote ? 'ADDING...' : 'ADD NOTE'}
            </button>
          </div>
          <div style={{ marginTop: '16px', borderTop: '1px solid #edf2f7', paddingTop: '12px' }}>
            <div style={{ fontWeight: 700, color: '#2d3748', fontSize: '13px', marginBottom: '8px' }}>
              Notes History ({projectNotes.length})
            </div>
            {projectNotes.length === 0 ? (
              <div style={{ color: '#a0aec0', fontSize: '13px' }}>No notes added yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {projectNotes.map((note) => (
                  <div
                    key={note._id || `${note.notedAt}-${note.text}`}
                    style={{
                      border: '1px solid #edf2f7',
                      borderRadius: '8px',
                      padding: '10px',
                      background: '#f8fafc',
                    }}
                  >
                    <div style={{ fontSize: '12px', color: '#718096', marginBottom: '6px' }}>
                      {(note.notedBy && typeof note.notedBy === 'object' ? note.notedBy.name : '') || 'Unknown user'}
                      {' - '}
                      {note.notedAt ? new Date(note.notedAt).toLocaleString() : '-'}
                    </div>
                    <div style={{ color: '#2d3748', fontSize: '13px', whiteSpace: 'pre-wrap' }}>{note.text || '-'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'History' && (
        <div style={{ ...cardStyle, marginTop: '12px' }}>
          <div style={{ marginBottom: '14px' }}>
            <span style={{ fontWeight: 700, fontSize: '15px', color: '#2d3748' }}>Project History</span>
          </div>

          {activityLoading ? (
            <div style={{ color: '#a0aec0', fontSize: '14px', textAlign: 'center', padding: '24px 0' }}>Loading…</div>
          ) : projectHistoryRows.length === 0 ? (
            <div style={{ color: '#a0aec0', fontSize: '14px', textAlign: 'center', padding: '24px 0' }}>
              No history recorded yet. Changes to this project will appear here.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '10px' }}>
              {projectHistoryRows.map((entry, idx) => {
                const HISTORY_FIELD_LABELS = {
                  name: 'Task Name',
                  status: 'Status',
                  priority: 'Priority',
                  taskCategory: 'Task Category',
                  hour: 'Estimated Hours',
                  actualHour: 'Total Actual Hours',
                  yourActualHour: 'Your Actual Hours',
                  taskDescription: 'Description',
                  startDate: 'Start Date',
                  dueDate: 'Due Date',
                  assignees: 'Assignees',
                  followers: 'Followers',
                  projectId: 'Project',
                  reminderRecipients: 'Reminder Sent To',
                  reminderMessage: 'Reminder Message',
                  customer: 'Customer',
                  deadline: 'Deadline',
                  description: 'Description',
                  totalEstimatedEffort: 'Estimated Effort',
                  teamLeader: 'Team Leader',
                  members: 'Members',
                  notes: 'Notes',
                };

                const ACTION_META = {
                  // Project-level
                  created:            { label: 'Created',            bg: '#f0fff4', border: '#9ae6b4', text: '#276749' },
                  updated:            { label: 'Updated',            bg: '#ebf8ff', border: '#bee3f8', text: '#2a69ac' },
                  deleted:            { label: 'Deleted',            bg: '#fff5f5', border: '#fed7d7', text: '#c53030' },
                  note_added:         { label: 'Note Added',         bg: '#f5f3ff', border: '#ddd6fe', text: '#5b21b6' },
                  // Feature-level
                  feature_added:      { label: 'Feature Added',      bg: '#e6fffa', border: '#81e6d9', text: '#234e52' },
                  feature_updated:    { label: 'Feature Updated',    bg: '#ebf8ff', border: '#bee3f8', text: '#2a69ac' },
                  feature_deleted:    { label: 'Feature Deleted',    bg: '#fff5f5', border: '#fed7d7', text: '#c53030' },
                  // Task-level
                  task_created:       { label: 'Task Created',       bg: '#f0fff4', border: '#9ae6b4', text: '#276749' },
                  task_updated:       { label: 'Task Updated',       bg: '#ebf8ff', border: '#bee3f8', text: '#2a69ac' },
                  task_deleted:       { label: 'Task Deleted',       bg: '#fff5f5', border: '#fed7d7', text: '#c53030' },
                  timer_stopped:      { label: 'Timer Stopped',      bg: '#fffaf0', border: '#fbd38d', text: '#975a16' },
                  attachment_added:   { label: 'Attachment Added',   bg: '#e6fffa', border: '#81e6d9', text: '#234e52' },
                  attachment_removed: { label: 'Attachment Removed', bg: '#fff5f5', border: '#fed7d7', text: '#c53030' },
                  comment_added:      { label: 'Comment Added',      bg: '#f5f3ff', border: '#ddd6fe', text: '#5b21b6' },
                  reminder:           { label: 'Reminder Sent',     bg: '#fffaf0', border: '#fbd38d', text: '#975a16' },
                };

                const ENTITY_BADGE = {
                  project: { label: 'Project', bg: '#e9d8fd', text: '#553c9a' },
                  task:    { label: 'Task',    bg: '#bee3f8', text: '#2a69ac' },
                  feature: { label: 'Feature', bg: '#c6f6d5', text: '#276749' },
                };

                // Normalise action key (backend uses 'updated'/'created' not 'task_updated')
                const rawAction = entry.action || '';
                const normalised = rawAction === 'updated' && entry.entityType === 'task' ? 'task_updated'
                  : rawAction === 'created' && entry.entityType === 'task' ? 'task_created'
                  : rawAction === 'deleted' && entry.entityType === 'task' ? 'task_deleted'
                  : rawAction;

                const { label, bg, border, text } = ACTION_META[normalised] || { label: rawAction, bg: '#f7fafc', border: '#e2e8f0', text: '#4a5568' };
                const entityBadge = ENTITY_BADGE[entry.entityType] || null;

                return (
                  <div key={entry._id || idx} style={{ border: `1px solid ${border}`, borderRadius: '10px', padding: '12px 14px', background: bg }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {entityBadge && (
                          <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px', background: entityBadge.bg, color: entityBadge.text, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {entityBadge.label}
                          </span>
                        )}
                        <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: border, color: text, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {label}
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#2d3748' }}>
                          {entry.changedBy?.name || 'Unknown user'}
                        </span>
                      </div>
                      <span style={{ fontSize: '12px', color: '#718096' }}>
                        {entry.changedAt ? new Date(entry.changedAt).toLocaleString() : '-'}
                      </span>
                    </div>
                    {entry.action !== 'reminder' && (
                      <div style={{ fontSize: '13px', color: '#2d3748', marginBottom: entry.changes?.length ? '8px' : 0 }}>
                        {entry.message || '-'}
                      </div>
                    )}
                    {entry.changes?.length > 0 && (
                      <div style={{ borderTop: `1px dashed ${border}`, paddingTop: '8px', marginTop: '4px' }}>
                        <div style={{ fontSize: '11px', color: text, fontWeight: 700, marginBottom: '6px' }}>CHANGED FIELDS</div>
                        <div style={{ display: 'grid', gap: '4px' }}>
                          {entry.changes.map((ch, ci) => {
                            const renderVal = (v) => {
                              if (v === undefined || v === null || v === '') return '(empty)';
                              if (Array.isArray(v)) {
                                return v
                                  .map((x) => (x && typeof x === 'object' ? (x.name || x.email || String(x)) : String(x)))
                                  .join(', ');
                              }
                              if (v && typeof v === 'object') return v.name || v.email || String(v);
                              if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(v)) {
                                return v.slice(0, 10);
                              }
                              return String(v);
                            };
                            return (
                             <div key={ci} style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '8px', fontSize: '12px', alignItems: 'start' }}>
                              <span style={{ color: '#4a5568', fontWeight: 600 }}>{HISTORY_FIELD_LABELS[ch.field] || ch.field}</span>
                              <span>
                                {ch.from === undefined || ch.from === '' ? (
                                  <span style={{ color: '#276749' }}>{renderVal(ch.to)}</span>
                                ) : (
                                  <>
                                    <span style={{ color: '#c53030', textDecoration: 'line-through' }}>{renderVal(ch.from)}</span>
                                    <span style={{ margin: '0 6px', color: '#4a5568' }}>→</span>
                                    <span style={{ color: '#276749' }}>{renderVal(ch.to)}</span>
                                  </>
                                )}
                              </span>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showAddTaskModal && (
        <div style={modalOverlayStyle}>
          <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#2d3748' }}>
                {editingTaskId ? 'Edit Task' : 'Add Task'}
              </h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => {
                  setShowAddTaskModal(false);
                  setPendingAttachments([]);
                  setExistingAttachments([]);
                }}
                style={modalCloseBtnStyle}
              >×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '10px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Task Name</div>
                <input
                  placeholder="Task Name"
                  value={newTask.name}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, name: e.target.value }))}
                  style={{ ...searchInputStyle, width: '100%', minWidth: 'unset' }}
                />
              </div>
              {editingTaskId && (
                <div>
                  <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Status</div>
                  <select
                    value={newTask.status}
                    onChange={(e) => setNewTask((prev) => ({ ...prev, status: e.target.value }))}
                    style={{ ...searchInputStyle, width: '100%', minWidth: 'unset' }}
                  >
                    {TASK_STATUSES.map((status) => (
                      <option key={status} value={status}>{STATUS_DISPLAY_LABEL[status] || status}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Priority</div>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, priority: e.target.value }))}
                  style={{ ...searchInputStyle, width: '100%', minWidth: 'unset' }}
                >
                  {PRIORITIES.map((priority) => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Task Category</div>
                <select
                  value={newTask.taskCategory}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, taskCategory: e.target.value }))}
                  style={{ ...searchInputStyle, width: '100%', minWidth: 'unset' }}
                >
                  {TASK_CATEGORIES.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Start Date</div>
                <input
                  type="date"
                  value={newTask.startDate}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, startDate: e.target.value }))}
                  style={{ ...searchInputStyle, width: '100%', minWidth: 'unset' }}
                />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Due Date</div>
                <input
                  type="date"
                  min={newTask.startDate || undefined}
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, dueDate: e.target.value }))}
                  style={{ ...searchInputStyle, width: '100%', minWidth: 'unset' }}
                />
              </div>
              <UserMultiSelectDropdown
                label="Assignees"
                placeholder="Select assignees"
                users={projectMembers}
                selectedIds={newTask.assignees}
                onChange={(ids) => setNewTask((prev) => ({ ...prev, assignees: ids }))}
              />
              <UserMultiSelectDropdown
                label="Followers"
                placeholder="Select followers"
                users={users}
                selectedIds={newTask.followers}
                onChange={(ids) => setNewTask((prev) => ({ ...prev, followers: ids }))}
              />
            </div>
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Task Description</div>
              <textarea
                value={newTask.taskDescription}
                onChange={(e) => setNewTask((prev) => ({ ...prev, taskDescription: e.target.value }))}
                style={{
                  ...searchInputStyle,
                  width: '100%',
                  minWidth: 'unset',
                  minHeight: '90px',
                  resize: 'vertical',
                  paddingTop: '8px',
                }}
                placeholder="Write task details..."
              />
            </div>
            {editingTaskId && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Comment</div>
                <textarea
                  placeholder="Add a comment"
                  value={newTask.comment}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, comment: e.target.value }))}
                  style={{
                    ...searchInputStyle,
                    width: '100%',
                    minWidth: 'unset',
                    height: '70px',
                    paddingTop: '10px',
                    resize: 'vertical',
                  }}
                />
              </div>
            )}
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Attachments</div>
              <input
                type="file"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) {
                    setPendingAttachments((prev) => {
                      // Only newly added files count toward the limit; existing
                      // attachments already on the task are ignored.
                      const remainingSlots = MAX_NEW_ATTACHMENTS - prev.length;
                      if (remainingSlots <= 0) {
                        toast.error(`You can upload up to ${MAX_NEW_ATTACHMENTS} new files.`);
                        return prev;
                      }
                      if (files.length > remainingSlots) {
                        toast.error(`You can upload up to ${MAX_NEW_ATTACHMENTS} new files.`);
                      }
                      return [...prev, ...files.slice(0, remainingSlots)];
                    });
                  }
                  e.target.value = '';
                }}
                style={{ ...searchInputStyle, width: '100%', minWidth: 'unset', height: 'auto', padding: '8px' }}
              />
              {pendingAttachments.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                  {pendingAttachments.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '12px',
                        color: '#4a5568',
                        background: '#f7fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '4px 8px',
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => setPendingAttachments((prev) => prev.filter((_, i) => i !== index))}
                        style={{ border: 'none', background: 'transparent', color: '#e53e3e', cursor: 'pointer', fontWeight: 700 }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              {editingTaskId && existingAttachments.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                  {existingAttachments.slice(0, VISIBLE_ATTACHMENT_LIMIT).map((file) => (
                    <div
                      key={file._id || file.url}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '12px',
                        background: '#ebf8ff',
                        border: '1px solid #bee3f8',
                        borderRadius: '6px',
                        padding: '4px 8px',
                      }}
                    >
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          textDecoration: 'none',
                          color: '#2b6cb0',
                          fontWeight: 600,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={file.fileName}
                      >
                        <MdAttachFile size={14} color="#1a202c" style={{ verticalAlign: '-3px', marginRight: '2px' }} />{file.fileName}
                      </a>
                      {file._id && (
                        <button
                          type="button"
                          aria-label={`Remove ${file.fileName}`}
                          title="Remove attachment"
                          onClick={() => handleRemoveExistingAttachment(file._id)}
                          disabled={removingAttachmentId === String(file._id)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#e53e3e',
                            cursor: removingAttachmentId === String(file._id) ? 'not-allowed' : 'pointer',
                            opacity: removingAttachmentId === String(file._id) ? 0.6 : 1,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >{removingAttachmentId === String(file._id) ? '…' : '🗑'}</button>
                      )}
                    </div>
                  ))}
                  {existingAttachments.length > VISIBLE_ATTACHMENT_LIMIT && (
                    <button
                      type="button"
                      onClick={() => navigate(projectManagementPaths.taskView(editingTaskId))}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        margin: '2px 0 0 2px',
                        color: '#2b6cb0',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textAlign: 'left',
                        alignSelf: 'flex-start',
                      }}
                      title="Open task to see all attachments"
                    >
                      See more ({existingAttachments.length - VISIBLE_ATTACHMENT_LIMIT})
                    </button>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
              <button
                type="button"
                onClick={() => {
                  setShowAddTaskModal(false);
                  setAssigneeActualHoursDraft({});
                  setPendingAttachments([]);
                  setExistingAttachments([]);
                }}
                style={toolbarBtnStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddTask}
                disabled={savingTask}
                style={{ ...toolbarBtnStyle, background: '#3182ce', color: 'white', borderColor: '#3182ce' }}
              >
                {savingTask ? 'Saving...' : (editingTaskId ? 'Update Task' : 'Add Task')}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedFile && (
        <div style={modalOverlayStyle} onClick={() => setSelectedFile(null)}>
          <div
            style={{ ...modalCardStyle, width: '1020px', maxWidth: '96vw', padding: 0, overflow: 'hidden' }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 600, color: '#2d3748', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedFile.fileName}
              </div>
              <button
                type="button"
                onClick={() => setSelectedFile(null)}
                title="Close"
                aria-label="Close"
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'transparent',
                  color: '#4a5568',
                  fontSize: '18px',
                  lineHeight: 1,
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 460px', minHeight: '560px' }}>
              <div style={{ background: '#f7fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
                {isImageFile(selectedFile) ? (
                  <img src={selectedFile.url} alt={selectedFile.fileName} style={{ maxWidth: '100%', maxHeight: '520px', objectFit: 'contain' }} />
                ) : (
                  <iframe
                    src={selectedFile.url}
                    title={selectedFile.fileName}
                    style={{ width: '100%', height: '520px', border: '1px solid #e2e8f0', borderRadius: '6px', background: 'white' }}
                  />
                )}
              </div>
              <div style={{ borderLeft: '1px solid #e2e8f0', padding: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#2d3748', marginBottom: '8px' }}>Add comment</div>
                <textarea
                  value={fileCommentText}
                  onChange={(event) => setFileCommentText(event.target.value)}
                  placeholder="Write your comment..."
                  style={{ width: '100%', minHeight: '90px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px', fontSize: '13px', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={handleAddFileComment}
                    disabled={submittingFileComment}
                    style={{ ...toolbarBtnStyle, background: '#3182ce', color: 'white', borderColor: '#3182ce' }}
                  >
                    {submittingFileComment ? 'Posting...' : 'Comment'}
                  </button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '14px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#2d3748' }}>Comments</div>
                  <div style={{ fontSize: '12px', color: '#718096' }}>{(selectedFile.comments || []).length}</div>
                </div>
                <div style={{ maxHeight: '360px', overflowY: 'auto', borderTop: '1px solid #edf2f7', paddingTop: '8px' }}>
                  {(selectedFile.comments || []).length === 0 ? (
                    <div style={{ color: '#a0aec0', fontSize: '13px', padding: '18px 0' }}>No comments</div>
                  ) : (
                    (selectedFile.comments || [])
                      .slice()
                      .sort((a, b) => new Date(b.commentedAt || 0) - new Date(a.commentedAt || 0))
                      .map((comment) => (
                        <div key={comment._id || `${comment.commentedAt}-${comment.text}`} style={{ borderBottom: '1px solid #f7fafc', padding: '8px 0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                            <div style={{ fontSize: '12px', color: '#2d3748', fontWeight: 600 }}>
                              {comment.commentedBy?.name || 'User'}
                              <span style={{ color: '#a0aec0', marginLeft: '6px', fontWeight: 400 }}>
                                {comment.commentedAt ? new Date(comment.commentedAt).toISOString().slice(0, 10) : '-'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                type="button"
                                title="Edit comment"
                                onClick={() => {
                                  setEditingFileCommentId(comment._id || '');
                                  setEditingFileCommentText(comment.text || '');
                                }}
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: '#e6fffa',
                                  color: '#234e52',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                }}
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                title="Delete comment"
                                onClick={() => handleDeleteFileComment(comment._id)}
                                disabled={deletingCommentId === comment._id}
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: '#fff5f5',
                                  color: '#c53030',
                                  cursor: deletingCommentId === comment._id ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  opacity: deletingCommentId === comment._id ? 0.6 : 1,
                                }}
                              >
                                {deletingCommentId === comment._id ? '…' : '🗑'}
                              </button>
                            </div>
                          </div>
                          {editingFileCommentId === comment._id ? (
                            <div style={{ marginTop: '6px' }}>
                              <textarea
                                value={editingFileCommentText}
                                onChange={(event) => setEditingFileCommentText(event.target.value)}
                                style={{ width: '100%', minHeight: '70px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px', fontSize: '13px', resize: 'vertical' }}
                              />
                              <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingFileCommentId('');
                                    setEditingFileCommentText('');
                                  }}
                                  style={{ ...toolbarBtnStyle, height: '28px' }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleUpdateFileComment}
                                  disabled={submittingFileComment}
                                  style={{ ...toolbarBtnStyle, height: '28px', background: '#3182ce', color: 'white', borderColor: '#3182ce' }}
                                >
                                  {submittingFileComment ? 'Saving...' : 'Save'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ marginTop: '3px', color: '#4a5568', fontSize: '13px', whiteSpace: 'pre-wrap' }}>{comment.text}</div>
                          )}
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddFeatureModal && (
        <div style={modalOverlayStyle} onClick={() => setShowAddFeatureModal(false)}>
          <div style={{ ...modalCardStyle, width: '560px', maxWidth: '94vw' }} onClick={(event) => event.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#2d3748' }}>
              {editingFeatureId ? 'Edit Feature / Epic' : 'Add Feature / Epic'}
            </h3>
            <div>
              <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Feature Name</div>
              <input
                placeholder="e.g. User Management Epic"
                value={newFeature.name}
                onChange={(e) => setNewFeature((prev) => ({ ...prev, name: e.target.value }))}
                style={{ ...searchInputStyle, width: '100%', minWidth: 'unset' }}
              />
            </div>
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Status</div>
              <select
                value={newFeature.status}
                onChange={(e) => setNewFeature((prev) => ({ ...prev, status: e.target.value }))}
                style={{ ...searchInputStyle, width: '100%', minWidth: 'unset' }}
              >
                {FEATURE_STATUSES.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Total Estimated Time</div>
              <input
                type="number"
                min="0"
                placeholder="0"
                value={newFeature.totalEstimatedTime}
                onChange={(e) => setNewFeature((prev) => ({ ...prev, totalEstimatedTime: e.target.value }))}
                style={{ ...searchInputStyle, width: '100%', minWidth: 'unset' }}
              />
            </div>
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Total Actual Time</div>
              <input
                type="number"
                min="0"
                placeholder="0"
                value={newFeature.totalActualTime}
                onChange={(e) => setNewFeature((prev) => ({ ...prev, totalActualTime: e.target.value }))}
                style={{ ...searchInputStyle, width: '100%', minWidth: 'unset' }}
              />
            </div>
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px', fontWeight: 600 }}>Description</div>
              <textarea
                value={newFeature.description}
                onChange={(e) => setNewFeature((prev) => ({ ...prev, description: e.target.value }))}
                style={{ ...searchInputStyle, width: '100%', minWidth: 'unset', minHeight: '90px', resize: 'vertical', paddingTop: '8px' }}
                placeholder="Describe this feature/epic..."
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
              <button
                type="button"
                onClick={() => {
                  setShowAddFeatureModal(false);
                  setEditingFeatureId('');
                  setNewFeature({ name: '', description: '', totalEstimatedTime: '', totalActualTime: '', status: 'Planned' });
                }}
                style={toolbarBtnStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveFeature}
                disabled={savingFeature}
                style={{ ...toolbarBtnStyle, background: '#3182ce', color: 'white', borderColor: '#3182ce' }}
              >
                {savingFeature ? 'Saving...' : (editingFeatureId ? 'Update Feature' : 'Add Feature')}
              </button>
            </div>
          </div>
        </div>
      )}

      <TaskReminderModal
        isOpen={Boolean(reminderTask)}
        taskName={reminderTask?.name}
        assignees={reminderTask ? getReminderAssignees(reminderTask) : []}
        currentUserId={currentUserId}
        sending={Boolean(remindingTaskId)}
        onClose={() => {
          if (!remindingTaskId) setReminderTask(null);
        }}
        onSubmit={submitReminder}
      />

      {Boolean(confirmDeleteTaskId) && (
        <div
          style={modalOverlayStyle}
          onClick={() => {
            if (!deletingTaskId) setConfirmDeleteTaskId('');
          }}
        >
          <div
            style={{ ...modalCardStyle, width: '420px', maxWidth: '92vw' }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#2d3748' }}>Delete Task</h3>
            <div style={{ color: '#4a5568', fontSize: '14px', lineHeight: 1.5 }}>
              Are you sure you want to delete this task? It will be moved to the Trash, where you can restore it or delete it permanently.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => setConfirmDeleteTaskId('')}
                disabled={Boolean(deletingTaskId)}
                style={toolbarBtnStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteTask({ _id: confirmDeleteTaskId })}
                disabled={Boolean(deletingTaskId)}
                style={{ ...toolbarBtnStyle, background: '#fff5f5', borderColor: '#feb2b2', color: '#c53030' }}
              >
                {deletingTaskId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {Boolean(confirmDeleteFeatureId) && (
        <div
          style={modalOverlayStyle}
          onClick={() => {
            if (!deletingFeatureId) setConfirmDeleteFeatureId('');
          }}
        >
          <div
            style={{ ...modalCardStyle, width: '420px', maxWidth: '92vw' }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#2d3748' }}>Delete Feature</h3>
            <div style={{ color: '#4a5568', fontSize: '14px', lineHeight: 1.5 }}>
              Delete this feature? Linked tasks will be unassigned from this feature.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => setConfirmDeleteFeatureId('')}
                disabled={Boolean(deletingFeatureId)}
                style={toolbarBtnStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteFeature(confirmDeleteFeatureId)}
                disabled={Boolean(deletingFeatureId)}
                style={{ ...toolbarBtnStyle, background: '#fff5f5', borderColor: '#feb2b2', color: '#c53030' }}
              >
                {deletingFeatureId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {Boolean(confirmDeleteProjectFileId) && (
        <div
          style={modalOverlayStyle}
          onClick={() => {
            if (!deletingProjectFileId) setConfirmDeleteProjectFileId('');
          }}
        >
          <div
            style={{ ...modalCardStyle, width: '420px', maxWidth: '92vw' }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#2d3748' }}>Delete File</h3>
            <div style={{ color: '#4a5568', fontSize: '14px', lineHeight: 1.5 }}>
              Are you sure you want to delete this file? This action cannot be undone.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => setConfirmDeleteProjectFileId('')}
                disabled={Boolean(deletingProjectFileId)}
                style={toolbarBtnStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteProjectFile(confirmDeleteProjectFileId)}
                disabled={Boolean(deletingProjectFileId)}
                style={{ ...toolbarBtnStyle, background: '#fff5f5', borderColor: '#feb2b2', color: '#c53030' }}
              >
                {deletingProjectFileId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const cardStyle = {
  background: 'white',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  padding: '12px 16px'
};

const overviewLayoutStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.7fr) minmax(280px, 1fr)',
  gap: '16px',
  marginTop: '12px',
  alignItems: 'start',
};

const overviewMainCardStyle = {
  background: '#fff',
  borderRadius: '14px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid #edf2f7',
  padding: '22px 24px',
};

const overviewHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  paddingBottom: '18px',
  marginBottom: '4px',
  borderBottom: '1px solid #edf2f7',
};

const overviewHeaderIconStyle = {
  width: '52px',
  height: '52px',
  borderRadius: '14px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.35)',
};

const overviewEyebrowStyle = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  color: '#805ad5',
  textTransform: 'uppercase',
};

const overviewTitleStyle = {
  fontSize: '22px',
  fontWeight: 800,
  color: '#1a202c',
  lineHeight: 1.2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const overviewSectionTitleStyle = {
  margin: '20px 0 8px',
  color: '#a0aec0',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const overviewDescriptionStyle = {
  color: '#4a5568',
  fontSize: '13px',
  lineHeight: 1.6,
  background: '#f8fafc',
  border: '1px solid #edf2f7',
  borderRadius: '10px',
  padding: '12px 14px',
};

const peopleGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '12px',
  marginTop: '18px',
};

const peopleCardStyle = {
  border: '1px solid #edf2f7',
  borderRadius: '12px',
  padding: '14px',
  background: '#fff',
};

const peopleCardTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  marginBottom: '12px',
  color: '#a0aec0',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const peopleRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '5px 0',
};

const peopleNameStyle = {
  color: '#2d3748',
  fontSize: '13px',
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const peopleEmptyStyle = {
  color: '#a0aec0',
  fontSize: '12px',
  fontStyle: 'italic',
  padding: '4px 0',
};

const memberSourceRadioStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  fontSize: '12px',
  color: '#4a5568',
  fontWeight: 600,
  cursor: 'pointer',
};

const leaderBadgeStyle = {
  marginLeft: 'auto',
  padding: '2px 9px',
  borderRadius: '999px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  fontSize: '10px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const overviewSidebarStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const statCardsRowStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '12px',
};

const statCardStyle = {
  border: '1px solid #edf2f7',
  borderRadius: '14px',
  padding: '16px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

const statIconStyle = {
  width: '34px',
  height: '34px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '10px',
};

const statValueStyle = {
  fontSize: '30px',
  fontWeight: 800,
  color: '#1a202c',
  lineHeight: 1,
};

const statLabelStyle = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#718096',
  marginTop: '6px',
};

const progressCardStyle = {
  background: '#fff',
  border: '1px solid #edf2f7',
  borderRadius: '14px',
  padding: '18px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

const progressCardTitleStyle = {
  fontSize: '13px',
  fontWeight: 700,
  color: '#2d3748',
  marginBottom: '16px',
};

const totalProgressBoxStyle = {
  textAlign: 'center',
  background: '#f0fff4',
  border: '1px solid #c6f6d5',
  borderRadius: '10px',
  padding: '12px',
};

const tabBarStyle = {
  display: 'flex',
  gap: '2px',
  flexWrap: 'wrap'
};

const tabStyle = {
  border: 'none',
  background: 'transparent',
  color: '#4a5568',
  fontSize: '13px',
  fontWeight: 600,
  padding: '10px 12px',
  borderBottom: 'none',
  cursor: 'pointer'
};

const activeTabStyle = {
  color: '#3182ce',
  borderBottom: '2px solid #3182ce'
};

const sectionTitleStyle = {
  margin: '0 0 10px',
  color: '#2d3748',
  fontSize: '15px',
  fontWeight: 700
};

const infoRowStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  padding: '8px 0',
  borderBottom: '1px solid #edf2f7',
  color: '#4a5568',
  fontSize: '13px'
};

const memberAvatarStyle = {
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontSize: '10px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const metricLabelStyle = {
  color: '#2d3748',
  fontWeight: 700,
  fontSize: '14px',
  marginBottom: '6px'
};

const progressTrackStyle = {
  height: '7px',
  width: '100%',
  borderRadius: '999px',
  background: '#edf2f7',
  overflow: 'hidden'
};

const progressFillStyle = {
  height: '100%',
  background: '#68d391'
};

const progressCircleOuter = {
  width: '130px',
  height: '130px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'conic-gradient(#68d391 0deg, #68d391 356deg, #edf2f7 356deg, #edf2f7 360deg)'
};

const progressCircleInner = {
  width: '96px',
  height: '96px',
  borderRadius: '50%',
  background: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#2d3748',
  fontWeight: 700,
  fontSize: '28px'
};

const taskSummaryCardStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  background: '#fff',
  padding: '16px',
  minHeight: '84px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

const toolbarBtnStyle = {
  height: '34px',
  border: '1px solid #cbd5e0',
  background: '#fff',
  borderRadius: '6px',
  padding: '0 12px',
  color: '#2d3748',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 600
};

// Reset button states, matching the Projects/Tasks list pages.
const resetBtnActiveStyle = {
  ...toolbarBtnStyle,
  border: 'none',
  background: 'rgb(254, 215, 215)',
  color: 'rgb(197, 48, 48)',
};

const resetBtnDisabledStyle = {
  ...toolbarBtnStyle,
  border: 'none',
  background: '#edf2f7',
  color: '#a0aec0',
  cursor: 'not-allowed',
};

const miniLinkBtnStyle = {
  border: 'none',
  background: 'transparent',
  color: '#3182ce',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '12px',
  padding: 0,
};

const overviewEditBtnStyle = {
  ...miniLinkBtnStyle,
  background: '#ebf8ff',
  color: '#1a56db',
  border: '1px solid #90cdf4',
  borderRadius: '999px',
  padding: '4px 10px',
  fontWeight: 700,
};

const searchInputStyle = {
  minWidth: '220px',
  height: '34px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  padding: '0 12px',
  fontSize: '13px',
  fontFamily: 'inherit'
};

const tableHeadStyle = {
  textAlign: 'left',
  padding: '10px 8px',
  fontSize: '11px',
  color: '#718096',
  fontWeight: 600
};

const tableCellStyle = {
  padding: '10px 8px',
  fontSize: '13px',
  color: '#4a5568',
  wordBreak: 'break-word'
};

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1200,
};

const modalCloseBtnStyle = {
  border: 'none',
  background: 'transparent',
  fontSize: '24px',
  lineHeight: 1,
  cursor: 'pointer',
  color: '#718096',
  padding: '2px 6px',
};

const modalCardStyle = {
  width: '700px',
  maxWidth: '94vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  background: '#fff',
  borderRadius: '10px',
  padding: '16px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
};

const userDropdownPanelStyle = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
  padding: '8px',
  zIndex: 30,
};

const selectAllBtnStyle = {
  border: '1px solid #cbd5e0',
  background: '#f7fafc',
  color: '#2d3748',
  borderRadius: '6px',
  padding: '4px 10px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  marginBottom: '8px',
};

const showMoreLinkStyle = {
  border: 'none',
  background: 'transparent',
  color: '#6b46c1',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
  padding: '4px 2px',
  width: 'fit-content',
};

const exportMenuStyle = {
  position: 'absolute',
  top: '36px',
  left: 0,
  minWidth: '120px',
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
  zIndex: 40,
  overflow: 'hidden',
};

const exportMenuItemStyle = {
  width: '100%',
  border: 'none',
  borderBottom: '1px solid #edf2f7',
  background: '#fff',
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: '12px',
  color: '#2d3748',
  cursor: 'pointer',
};

export default ProjectViewPage;
